import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, Navigate, useLocation as useRouteLoc, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, Package, MapPin, Users, ClipboardList,
  Database, LogOut, Plus, Search, AlertTriangle, ShieldAlert,
  Wrench, Activity, ChevronRight, ChevronLeft, Edit3, Trash2, X, Download, Paperclip, Check,
  Building2, FileText, Upload, Sun, Moon, Layers, BarChart3, Eye, EyeOff, ArrowRight,
  UserRound, Lock, ShieldCheck,
} from "lucide-react";
import { api, getUser, setSession, clearSession, isAdmin, canEditAtLocation } from "./lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

// ---------------- Common UI ----------------
const Card = ({ children, className = "", "data-testid": testId, ...rest }) => (
  <div className={`card ${className}`} data-testid={testId} {...rest}>{children}</div>
);

const Btn = ({ children, onClick, variant = "primary", disabled, type = "button", className = "", "data-testid": testId, ...rest }) => {
  const cls = {
    primary: "btn-primary",
    secondary: "btn-secondary",
    danger: "btn-danger",
    ghost: "btn-ghost",
  }[variant] || "btn-primary";
  return (
    <button type={type} disabled={disabled} onClick={onClick} data-testid={testId}
      className={`${cls} ${className}`} {...rest}>
      {children}
    </button>
  );
};

const Input = (p) => <input {...p} className={`input ${p.className || ""}`} />;
const Select = (p) => <select {...p} className={`input bg-white ${p.className || ""}`}>{p.children}</select>;
const Label = ({ children }) => <label className="label">{children}</label>;
const Field = ({ label, children }) => <div><Label>{label}</Label>{children}</div>;

const Badge = ({ children, color = "slate" }) => {
  const cls = {
    slate: "pill-slate", green: "pill-emerald", emerald: "pill-emerald",
    red: "pill-rose", rose: "pill-rose", amber: "pill-amber",
    indigo: "pill-indigo", sky: "pill-sky",
  }[color] || "pill-slate";
  return <span className={cls}>{children}</span>;
};

// ---------------- Auth guard ----------------
function useAuthGuard() {
  const nav = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem("uat_token")) nav("/login");
  }, [nav]);
  return getUser();
}

// ---------------- Login ----------------
function LoginPage() {
  const nav = useNavigate();
  const [u, setU] = useState(""); const [p, setP] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [caps, setCaps] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.login(u, p);
      setSession(data.access_token, data.user);
      const next = localStorage.getItem("uat_post_login");
      if (next) {
        localStorage.removeItem("uat_post_login");
        nav(next, { replace: true });
      } else {
        nav("/");
      }
    } catch (e2) { setErr(e2.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  // Plant photo lives at public/aampl-building.jpg. If it's ever missing, the
  // panel falls back to the dark gradient rather than breaking the layout.
  const capabilities = ["Asset register", "Service logs", "QR tagging", "Expiry alerts", "Spend reports"];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070b18]" data-testid="login-page">
      {/* The plant photo IS the page background. Fixed to the viewport rather
          than the document, so a taller page never crops it further. */}
      <img
        src="/aampl-building.jpg"
        alt="AAMPL plant"
        className="fixed inset-0 h-full w-full object-cover object-center"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid="login-hero-image"
      />
      {/* Scrims kept light: dark enough at the edges to seat the type,
          clear through the middle so the building reads as the backdrop. */}
      <div className="fixed inset-0 bg-gradient-to-r from-[#070b18]/88 via-[#070b18]/20 to-[#070b18]/80" />
      <div className="fixed inset-x-0 top-0 h-32 bg-gradient-to-b from-[#070b18]/70 to-transparent" />
      <div className="fixed inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#070b18]/80 to-transparent" />
      <div className="pointer-events-none fixed -left-40 top-1/4 h-[32rem] w-[32rem] rounded-full bg-indigo-600/15 blur-[140px]" />

      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-6 sm:px-10 lg:px-12">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 rounded-xl border border-white/15 bg-black/35 px-3 py-2 backdrop-blur-md">
            <img src="/aampl-logo.png" alt="AAMPL" className="h-8 w-auto rounded bg-white p-1"
                 onError={(e)=>{ e.currentTarget.style.display='none'; }} />
            <div className="leading-tight">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">Advanced Anmol Metcomp</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-indigo-300">Utilities &amp; Assets</div>
            </div>
          </div>
          <span className="hidden items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300 backdrop-blur-md sm:inline-flex">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Asset system · Online
          </span>
        </header>

        <div className="grid flex-1 items-center gap-8 py-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">

          {/* ---------- Headline, straight on the photo ---------- */}
          <div className="order-2 lg:order-1">
            <h1 className="text-4xl font-bold leading-[1.06] tracking-tight text-white drop-shadow-2xl sm:text-5xl xl:text-[3.4rem]">
              Every asset,<br /><span className="bg-gradient-to-r from-indigo-300 to-sky-300 bg-clip-text text-transparent drop-shadow-none">accounted for.</span>
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-200 drop-shadow-lg sm:text-[15px]">
              Registers, service logs, QR tagging, warranty &amp; AMC tracking and spend
              reports across every plant.
            </p>

            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300/80 drop-shadow">
              {capabilities.map(c => <span key={c}>{c}</span>)}
            </div>
          </div>
          {/* ---------- Sign-in: glass card with a slow lit border ---------- */}
          <div className="rise-in order-1 w-full max-w-md justify-self-center lg:order-2 lg:justify-self-end">
            <div className="relative overflow-hidden rounded-[1.75rem] p-[1.5px]">
              {/* Conic sweep sits behind the card and shows only at the 1.5px edge. */}
              <div className="border-sweep absolute inset-[-120%] bg-[conic-gradient(from_0deg,transparent_0deg,rgba(99,102,241,0.9)_50deg,transparent_110deg,transparent_250deg,rgba(56,189,248,0.75)_300deg,transparent_360deg)]" />
              <div className="relative rounded-[1.65rem] bg-[#0a0f1f]/95 p-8 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.95)] backdrop-blur-2xl">
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-indigo-500/20 blur-3xl" />

                <div className="relative">
                  <div className="mb-6 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-indigo-300">
                    <span className="h-px w-8 bg-gradient-to-r from-indigo-400 to-transparent" />
                    Utilities &amp; Assets
                  </div>
                  <h2 className="text-[2.1rem] font-bold leading-tight tracking-tight text-white">Sign in</h2>
                  <p className="mt-1.5 text-sm text-slate-400">Welcome back — use your AAMPL credentials.</p>

                  <form onSubmit={submit} className="mt-7 space-y-4">
                    {/* Floating-label fields: the label rides up once there's a value. */}
                    <div className="group/field relative">
                      <UserRound size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 transition group-focus-within/field:text-indigo-400" />
                      <input
                        id="login-username"
                        value={u}
                        onChange={(e)=>setU(e.target.value)}
                        autoFocus
                        autoComplete="username"
                        placeholder=" "
                        className="login-field peer w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 pb-2.5 pl-11 pt-6 text-sm text-white outline-none transition focus:border-indigo-400/60 focus:bg-white/[0.07] focus:ring-4 focus:ring-indigo-500/15"
                        data-testid="login-username-input"
                      />
                      <label
                        htmlFor="login-username"
                        className="pointer-events-none absolute left-11 top-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-300/90 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-slate-500 peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-[0.14em] peer-focus:text-indigo-300"
                      >
                        Username
                      </label>
                    </div>

                    <div className="group/field relative">
                      <Lock size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 transition group-focus-within/field:text-indigo-400" />
                      <input
                        id="login-password"
                        type={showPw ? "text" : "password"}
                        value={p}
                        onChange={(e)=>setP(e.target.value)}
                        onKeyUp={(e)=>setCaps(e.getModifierState && e.getModifierState("CapsLock"))}
                        autoComplete="current-password"
                        placeholder=" "
                        className="login-field peer w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 pb-2.5 pl-11 pr-12 pt-6 text-sm text-white outline-none transition focus:border-indigo-400/60 focus:bg-white/[0.07] focus:ring-4 focus:ring-indigo-500/15"
                        data-testid="login-password-input"
                      />
                      <label
                        htmlFor="login-password"
                        className="pointer-events-none absolute left-11 top-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-indigo-300/90 transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-slate-500 peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-[0.14em] peer-focus:text-indigo-300"
                      >
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={()=>setShowPw(s => !s)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-indigo-300"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        data-testid="login-password-toggle"
                      >
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>

                    {caps && !err && (
                      <div className="flex items-center gap-2 text-[11px] text-amber-300/90" data-testid="login-capslock">
                        <ShieldAlert size={13} /> Caps Lock is on
                      </div>
                    )}

                    {err && (
                      <div className="flex items-center gap-2 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs text-rose-300" data-testid="login-error">
                        <ShieldAlert size={14} className="flex-shrink-0" /> {err}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={busy}
                      className="group/btn relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-4 text-sm font-semibold text-white shadow-[0_18px_40px_-12px_rgba(79,70,229,0.8)] transition hover:shadow-[0_22px_50px_-12px_rgba(99,102,241,0.9)] disabled:cursor-not-allowed disabled:opacity-60"
                      data-testid="login-submit-btn"
                    >
                      {/* Sheen that sweeps across on hover. */}
                      <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full" />
                      <span className="relative flex items-center justify-center gap-2">
                        {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} className="transition group-hover/btn:translate-x-1" /></>}
                      </span>
                    </button>
                  </form>

                  <div className="mt-7 flex items-center gap-3 border-t border-white/10 pt-5 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px] shadow-emerald-400" /> Secured connection
                    </span>
                    <span className="ml-auto uppercase tracking-[0.14em] text-slate-600">UAT-F-01</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-5 text-center text-[11px] tracking-wide text-slate-600">
              AAMPL · Internal use only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Layout / Shell ----------------
function useTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("uat_theme");
      if (saved === "light" || saved === "dark") return saved;
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
    } catch { /* ignore */ }
    return "light";
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("uat_theme", theme); } catch { /* ignore */ }
  }, [theme]);
  const toggle = () => setTheme(t => (t === "dark" ? "light" : "dark"));
  return { theme, toggle };
}

function Shell({ children }) {
  const user = useAuthGuard();
  const nav = useNavigate();
  const route = useRouteLoc();
  const { theme, toggle: toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("uat_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggle = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem("uat_sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  if (!user) return null;
  const items = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/assets", label: "Assets", icon: Package, testid: "nav-assets" },
    { to: "/maintenance", label: "Service logs", icon: Wrench, testid: "nav-maintenance" },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
    { to: "/locations", label: "Locations", icon: MapPin, admin: true, testid: "nav-locations" },
    { to: "/areas", label: "Areas", icon: Building2, admin: true, testid: "nav-areas" },
    { to: "/categories", label: "Categories", icon: Layers, admin: true, testid: "nav-categories" },
    { to: "/users", label: "Users", icon: Users, admin: true, testid: "nav-users" },
    { to: "/audit", label: "Audit log", icon: ClipboardList, admin: true, testid: "nav-audit" },
    { to: "/backup", label: "Backup & restore", icon: Database, admin: true, testid: "nav-backup" },
  ].filter(i => !i.admin || user.role === "admin");
  const logout = () => { clearSession(); nav("/login"); };
  const initials = (user.name || user.username || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isDark = theme === "dark";
  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950" data-testid="app-shell">
      <aside
        className={`${collapsed ? "w-16" : "w-60"} bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-200`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className={`px-3 py-5 border-b border-slate-100 dark:border-slate-800 flex items-center ${collapsed ? "justify-center" : "gap-2.5 px-5"}`}>
          <img src="/aampl-logo.png" alt="AAMPL" className="h-7 w-auto flex-shrink-0"
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 dark:text-slate-100 leading-tight truncate">AAMPL</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 leading-tight truncate">Utilities &amp; Assets</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={toggle}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              className="text-slate-400 dark:text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="sidebar-collapse-btn"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={toggle}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            className="mx-2 mt-2 p-1.5 rounded text-slate-400 dark:text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition flex justify-center"
            data-testid="sidebar-expand-btn"
          >
            <ChevronRight size={16} />
          </button>
        )}
        <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-0.5`}>
          {items.map(({ to, label, icon: Icon, testid }) => {
            const active = (to === "/" ? route.pathname === "/" : route.pathname.startsWith(to));
            return (
              <Link key={to} to={to} data-testid={testid}
                title={collapsed ? label : undefined}
                className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"} px-3 py-2 rounded-lg text-sm transition ${
                  active
                    ? "bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-500/15 dark:text-indigo-300"
                    : "text-slate-600 dark:text-slate-300 dark:text-slate-300 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}>
                <Icon size={16} strokeWidth={active ? 2.25 : 2} />
                {!collapsed && <span className="truncate">{label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className={`border-t border-slate-100 dark:border-slate-800 ${collapsed ? "p-2" : "px-3 py-2"}`}>
          <button
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle-btn"
            data-theme={theme}
            className={`flex items-center ${collapsed ? "justify-center w-full" : "gap-2 w-full"} px-2 py-1.5 rounded-lg text-xs text-slate-600 dark:text-slate-300 dark:text-slate-300 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition`}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
            {!collapsed && <span className="truncate">{isDark ? "Light mode" : "Dark mode"}</span>}
          </button>
        </div>

        <div className={`border-t border-slate-100 dark:border-slate-800 ${collapsed ? "p-2" : "p-3"}`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"} p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition`}
               title={collapsed ? `${user.name} · ${user.role.replace('_', ' ')}` : undefined}>
            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 text-xs font-semibold flex items-center justify-center flex-shrink-0">{initials}</div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-900 dark:text-slate-100 dark:text-slate-100 dark:text-slate-100 truncate" data-testid="current-user-name">{user.name}</div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 capitalize">{user.role.replace('_', ' ')}</div>
                </div>
                <button onClick={logout} aria-label="Sign out" data-testid="logout-btn"
                        className="text-slate-400 dark:text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition">
                  <LogOut size={14} />
                </button>
              </>
            )}
          </div>
          {collapsed && (
            <button onClick={logout} aria-label="Sign out" title="Sign out" data-testid="logout-btn"
                    className="w-full mt-1 flex justify-center p-2 rounded-lg text-slate-400 dark:text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
              <LogOut size={14} />
            </button>
          )}
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
}

// ---------------- Dashboard ----------------
function Dashboard() {
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    Promise.all([api.dashboardStats(), api.expiring(30), api.listLocations(), api.listCategories()])
      .then(([s, e, l, c]) => { setStats(s.data); setExpiring(e.data); setLocations(l.data); setCategories(c.data); });
  }, []);
  if (!stats) return <div className="p-8 text-slate-500" data-testid="dashboard-loading">Loading…</div>;
  const locName = (id) => locations.find(l => l.id === id)?.name || "—";
  const catName = (code) => categories.find(c => c.code === code)?.name || code;
  const isAdminUser = user?.role === "admin";

  const PALETTE = ["#534AB7", "#1D9E75", "#D85A30", "#D4537E", "#BA7517", "#378ADD", "#888780", "#0F6E56", "#993556", "#A32D2D"];
  const catEntries = Object.entries(stats.by_category || {}).sort((a,b)=>b[1]-a[1]);
  const catChart = catEntries.slice(0, 6).map(([code, n]) => ({ name: catName(code), value: n }));
  const otherSum = catEntries.slice(6).reduce((s, [,n]) => s + n, 0);
  if (otherSum > 0) catChart.push({ name: "Other", value: otherSum });

  const locEntries = Object.entries(stats.by_location || {})
    .map(([id, n]) => ({ name: locName(id), value: n }))
    .sort((a,b)=>b.value-a.value);
  const maxLoc = locEntries.reduce((m, x) => Math.max(m, x.value), 1);

  return (
    <div className="p-8 space-y-5" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 mt-1">{stats.total_assets} assets across {stats.total_locations} locations</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Link to="/assets" className="block" data-testid="kpi-link-total-assets">
          <KPICard label="Total assets" value={stats.total_assets} icon={Package} accent="text-indigo-500" hint="View all" testid="kpi-total-assets" />
        </Link>
        {isAdminUser ? (
          <Link to="/locations" className="block" data-testid="kpi-link-total-locations">
            <KPICard label="Locations" value={stats.total_locations} icon={MapPin} accent="text-slate-500" hint="Manage" testid="kpi-total-locations" />
          </Link>
        ) : (
          <KPICard label="Locations" value={stats.total_locations} icon={MapPin} accent="text-slate-500" testid="kpi-total-locations" />
        )}
        <Link to="/maintenance" className="block" data-testid="kpi-link-total-maintenance">
          <KPICard label="Service logs" value={stats.total_maintenance_logs} icon={Wrench} accent="text-emerald-500" hint="View all" testid="kpi-total-maintenance" />
        </Link>
        {isAdminUser ? (
          <Link to="/users" className="block" data-testid="kpi-link-total-users">
            <KPICard label="Active users" value={stats.total_users} icon={Users} accent="text-amber-500" hint="Manage" testid="kpi-total-users" />
          </Link>
        ) : (
          <KPICard label="Active users" value={stats.total_users} icon={Users} accent="text-amber-500" testid="kpi-total-users" />
        )}
      </div>

      <div className="grid grid-cols-5 gap-3">
        <Card className="p-5 col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Assets by category</h2>
            <span className="text-[11px] text-slate-400 dark:text-slate-500 dark:text-slate-500 dark:text-slate-500">{catEntries.length} categories</span>
          </div>
          <div className="flex items-center gap-4" data-testid="assets-by-category">
            <div className="w-44 h-44 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={catChart} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={1} strokeWidth={0}>
                    {catChart.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <RTooltip contentStyle={{
  fontSize: 12, borderRadius: 8,
  border: '1px solid var(--rt-border, #e2e8f0)',
  background: 'var(--rt-bg, #ffffff)',
  color: 'var(--rt-text, #0f172a)',
}} wrapperStyle={{ outline: 'none' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-1.5 text-xs">
              {catChart.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{background: PALETTE[i % PALETTE.length]}} />
                    <span className="truncate text-slate-700">{c.name}</span>
                  </div>
                  <span className="text-slate-500 tabular-nums">{c.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-5 col-span-2">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-500" /> Expiring within 30 days
          </h2>
          <div className="space-y-2">
            <Link to="/assets?expiring_warranty_days=30&status=Active" className="block" data-testid="exp-link-warranty">
              <AlertRow icon={ShieldAlert} label="Warranty" n={stats.expiring_30d?.warranty || 0} tone="amber" testid="exp-warranty" />
            </Link>
            <Link to="/assets?expiring_amc_days=30&status=Active" className="block" data-testid="exp-link-amc">
              <AlertRow icon={ClipboardList} label="AMC" n={stats.expiring_30d?.amc || 0} tone="amber" testid="exp-amc" />
            </Link>
            <Link to="/assets?expiring_service_days=30&status=Active" className="block" data-testid="exp-link-service">
              <AlertRow icon={Wrench} label="Service due" n={stats.expiring_30d?.service_due || 0} tone="rose" testid="exp-service" />
            </Link>
            <Link to="/assets?expiring_insurance_days=30&status=Active" className="block" data-testid="exp-link-insurance">
              <AlertRow icon={Activity} label="Vehicle insurance" n={stats.expiring_30d?.vehicle_insurance || 0} tone="sky" testid="exp-insurance" />
            </Link>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Assets by location</h2>
        <div className="space-y-2.5">
          {locEntries.map(({ name, value }) => (
            <div key={name}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-700">{name}</span>
                <span className="text-slate-500 tabular-nums">{value}</span>
              </div>
              <div className="bar-track h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(value/maxLoc)*100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5" data-testid="recent-maintenance-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2">
            <Wrench size={14} className="text-emerald-500" /> Recent maintenance
          </h2>
          <Link to="/maintenance" className="text-xs text-indigo-600 hover:underline" data-testid="recent-maintenance-view-all">View all →</Link>
        </div>
        <RecentMaintenance catName={catName} locName={locName} />
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2">
            <AlertTriangle size={14} className="text-rose-500" /> Overdue service
          </h2>
          {(expiring?.overdue_service?.length || 0) > 0 && (
            <span className="pill-rose">{expiring.overdue_service.length} overdue</span>
          )}
        </div>
        {!expiring?.overdue_service?.length ? (
          <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 py-4">Nothing overdue. Nice work.</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800 dark:divide-slate-800">
            {expiring.overdue_service.slice(0, 8).map(a => (
              <Link key={a.id} to={`/assets/${a.id}`} className="flex items-center justify-between py-3 hover:bg-slate-50/60 dark:bg-slate-800/40 dark:bg-slate-800/40 -mx-2 px-2 rounded-lg transition">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                    <Wrench size={15} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-100">{a.asset_id} · {a.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">{catName(a.category_code)} · {locName(a.location_id)}</div>
                  </div>
                </div>
                <div className="text-xs text-rose-600 font-medium">{fmtDMY(a.next_service_due)}</div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, accent = "text-slate-400 dark:text-slate-500 dark:text-slate-500", valueClass = "", sub, hint, testid }) {
  return (
    <div className={`card-padded h-full ${hint ? "transition hover:border-indigo-200 hover:shadow-card cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
        <Icon size={15} className={accent} />
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${valueClass}`} data-testid={testid}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 dark:text-slate-500 mt-1">{sub}</div>}
      {hint && <div className="text-[11px] text-indigo-600 mt-2 flex items-center gap-1">{hint} <ChevronRight size={11} /></div>}
    </div>
  );
}

function AlertRow({ icon: Icon, label, n, tone, testid }) {
  const bg = { amber: "bg-amber-50", rose: "bg-rose-50", sky: "bg-sky-50" }[tone] || "bg-slate-50";
  const text = { amber: "text-amber-700", rose: "text-rose-700", sky: "text-sky-700" }[tone] || "text-slate-700";
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${bg} transition hover:ring-1 hover:ring-slate-200 cursor-pointer`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={text} />
        <span className="text-sm text-slate-700 dark:text-slate-200 dark:text-slate-200">{label}</span>
      </div>
      <span className={`text-sm font-semibold tabular-nums ${text}`} data-testid={testid}>{n}</span>
    </div>
  );
}

function RecentMaintenance({ catName, locName }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    api.recentMaintenance(8).then(r => setLogs(r.data)).catch(() => setLogs([]));
  }, []);
  if (logs === null) return <div className="text-sm text-slate-400 dark:text-slate-500 dark:text-slate-500 py-4" data-testid="recent-maintenance-loading">Loading…</div>;
  if (logs.length === 0) {
    return (
      <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 py-6 text-center" data-testid="recent-maintenance-empty">
        No maintenance logs recorded yet.
        <div className="text-[11px] text-slate-400 dark:text-slate-500 dark:text-slate-500 dark:text-slate-500 mt-1">Open any asset and click "Add Log" to record a service entry.</div>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800 dark:divide-slate-800" data-testid="recent-maintenance-list">
      {logs.map(l => {
        const a = l.asset;
        return (
          <Link
            key={l.id}
            to={a ? `/assets/${a.id}` : "/maintenance"}
            className="flex items-center justify-between py-3 -mx-2 px-2 rounded-lg hover:bg-slate-50/60 dark:bg-slate-800/40 dark:bg-slate-800/40 transition"
            data-testid={`recent-maintenance-row-${l.id}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 flex items-center justify-center flex-shrink-0">
                <Wrench size={15} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-100 truncate">
                  {a ? `${a.asset_id} · ${a.name}` : "(asset deleted)"}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 truncate">
                  {a ? `${catName(a.category_code)} · ${locName(a.location_id)}` : "—"}
                  {l.service_done_by && ` · by ${l.service_done_by}`}
                </div>
              </div>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <div className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">{fmtDMY(l.date_of_service)}</div>
              {l.amount > 0 && <div className="text-xs font-medium text-slate-700 tabular-nums">₹{l.amount.toLocaleString("en-IN")}</div>}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// ---------------- Assets list ----------------
// Filter/page state for the assets list lives in the URL so that coming back
// (browser Back, or "← Back to assets") lands on the exact same view.
const ASSET_FILTER_DEFAULTS = { category_code: "", location_id: "", area_id: "", department: "", status: "Active", q: "" };
const ASSET_SCROLL_KEY = "uat_assets_scroll";

function AssetsPage() {
  const user = getUser();
  const route = useRouteLoc();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const filters = useMemo(() => {
    const out = {};
    for (const k of Object.keys(ASSET_FILTER_DEFAULTS)) {
      const v = searchParams.get(k);
      out[k] = v == null ? ASSET_FILTER_DEFAULTS[k] : v;
    }
    return out;
  }, [searchParams]);
  // Merge a filter patch back into the URL. Replace (not push) so typing in the
  // search box doesn't bury the previous page under dozens of history entries.
  const setFilters = (patch) => {
    const merged = typeof patch === "function" ? patch(filters) : { ...filters, ...patch };
    const next = new URLSearchParams(searchParams);
    for (const [k, def] of Object.entries(ASSET_FILTER_DEFAULTS)) {
      const v = merged[k] ?? "";
      // status is the one filter whose default isn't empty: "" means "All".
      if (v === def) next.delete(k); else next.set(k, v);
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };
  // Categories whose contract dates matter at a glance get them as list columns,
  // in place of Area/Department/User.
  const AMC_COLUMNS = [
    { field: "amc_from", label: "AMC From" },
    { field: "amc_to", label: "AMC To", due: true },
  ];
  const CONTEXT_COLUMNS = {
    VEHICLE: [
      { field: "insurance_from", label: "Insurance From" },
      { field: "insurance_to", label: "Insurance To", due: true },
    ],
    PASSENGER_LIFT: AMC_COLUMNS,
    RO_PLANT: AMC_COLUMNS,
  };
  const contextCols = CONTEXT_COLUMNS[filters.category_code] || null;
  const fmtCtxDate = (v) => {
    if (!v) return "—";
    const s = String(v).slice(0, 10);
    const p = s.split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
  };
  const ctxDueClass = (v) => {
    if (!v) return "text-slate-600 dark:text-slate-300";
    const d = new Date(String(v).slice(0, 10));
    if (isNaN(d.getTime())) return "text-slate-600 dark:text-slate-300";
    const days = Math.floor((d.getTime() - Date.now()) / 86400000);
    if (days < 0) return "text-rose-600 dark:text-rose-400 font-semibold";
    if (days <= 30) return "text-amber-600 dark:text-amber-400 font-semibold";
    return "text-slate-600 dark:text-slate-300";
  };
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const setPage = (updater) => {
    const nextPage = typeof updater === "function" ? updater(page) : updater;
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page"); else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  };
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showMove, setShowMove] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Expiring filters come from the URL (dashboard tiles and report cells deep-link here).
  const expiringParams = useMemo(() => {
    const out = {};
    for (const k of ["expiring_warranty_days","expiring_amc_days","expiring_service_days","expiring_insurance_days","overdue_service","expired"]) {
      const v = searchParams.get(k);
      if (v == null) continue;
      if (k === "overdue_service") out[k] = v === "true";
      else if (k === "expired") out[k] = v;
      else out[k] = parseInt(v, 10);
    }
    return out;
  }, [searchParams]);
  // Remember where the list was scrolled to, keyed by the exact list URL, so the
  // detail page → back trip returns to the same row rather than the top.
  const listUrl = route.pathname + route.search;
  // Snapshot the offset as the row is clicked. Doing it on unmount records 0
  // instead: the short detail page renders first and the browser clamps scroll.
  const rememberScroll = () => {
    try { sessionStorage.setItem(ASSET_SCROLL_KEY, JSON.stringify({ url: listUrl, y: window.scrollY })); } catch { /* ignore */ }
  };
  const scrollRestored = useRef(false);
  useEffect(() => {
    if (scrollRestored.current || !items.length) return;
    scrollRestored.current = true;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(ASSET_SCROLL_KEY) || "null"); } catch { /* ignore */ }
    if (!saved || saved.url !== listUrl || !(saved.y > 0)) return;
    // Retry briefly: the table may still be growing, and the browser does its own
    // scroll handling on popstate that can land after our first attempt.
    let tries = 0;
    const tick = () => {
      window.scrollTo(0, saved.y);
      if (++tries < 6 && Math.abs(window.scrollY - saved.y) > 2) setTimeout(tick, 60);
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const load = () => {
    const p = { ...filters, ...expiringParams, page, page_size: 50 };
    Object.keys(p).forEach(k => (p[k] === "" || p[k] == null) && delete p[k]);
    api.listAssets(p).then(r => { setItems(r.data.items); setTotal(r.data.total); setSelected(new Set()); });
  };
  useEffect(() => {
    api.listCategories().then(r => setCategories(r.data));
    api.listLocations().then(r => setLocations(r.data));
    api.listAreas().then(r => setAreas(r.data)).catch(()=>setAreas([]));
  }, []);
  useEffect(() => {
    // Wait for the areas list before judging an area_id restored from the URL.
    if (!areas.length) return;
    if (filters.area_id && !areas.find(a => a.id === filters.area_id && a.location_id === filters.location_id)) {
      setFilters(f => ({ ...f, area_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location_id, areas]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [JSON.stringify(filters), page, JSON.stringify(expiringParams)]);

  const canCreate = user?.role === "admin" || user?.role === "location_editor";
  const isAdminUser = user?.role === "admin";
  const locName = (id) => locations.find(l => l.id === id)?.name || "—";
  const catName = (code) => categories.find(c => c.code === code)?.name || code;
  const areaName = (id) => areas.find(a => a.id === id)?.name || "";

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(i => i.id)));
  };

  // Export exactly what the current filters produce — every row, not just this page.
  const exportList = async () => {
    setExporting(true);
    try {
      const params = { ...filters, ...expiringParams };
      Object.keys(params).forEach(k => (params[k] === "" || params[k] == null) && delete params[k]);
      const { data: blob } = await api.exportAssetRegister(params);
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.href = url;
      el.download = `assets-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Timer schedules only mean anything for ACs — show the column when they're on screen.
  const showTimerCol = filters.category_code === "AIR_CONDITIONER"
    || items.some(a => a.category_code === "AIR_CONDITIONER");

  return (
    <div className="p-8 space-y-6" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100" data-testid="assets-page-title">Assets <span className="text-base font-normal text-slate-400 dark:text-slate-500 dark:text-slate-500">({total})</span></h1>
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={exportList} disabled={exporting || !total}
               title={total ? `Download these ${total} asset(s) as Excel` : "Nothing to export"}
               data-testid="export-assets-btn">
            <Download size={14} className="inline mr-1" />{exporting ? "Preparing…" : "Export Excel"}
          </Btn>
          {isAdminUser && (
            <Link to="/qr-sheet" data-testid="print-qr-sheet-link"><Btn variant="secondary">Print QR Sheet</Btn></Link>
          )}
          {canCreate && <Btn onClick={() => setShowCreate(true)} data-testid="new-asset-btn"><Plus size={16} className="inline mr-1" />New Asset</Btn>}
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-4 gap-3">
          <Field label="Category">
            <Select value={filters.category_code} onChange={e=>setFilters(f=>({...f,category_code:e.target.value}))} data-testid="filter-category">
              <option value="">All categories</option>
              {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Location">
            <Select value={filters.location_id} onChange={e=>setFilters(f=>({...f,location_id:e.target.value}))} data-testid="filter-location">
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label="Area">
            <Select
              value={filters.area_id}
              disabled={!filters.location_id}
              onChange={e=>setFilters(f=>({...f,area_id:e.target.value}))}
              data-testid="filter-area"
            >
              <option value="">{filters.location_id ? "All areas" : "Pick a location first"}</option>
              {areas.filter(a => a.location_id === filters.location_id).map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Department">
            <Input
              value={filters.department}
              onChange={e=>setFilters(f=>({...f,department:e.target.value}))}
              placeholder="e.g. IT, HR…"
              data-testid="filter-department"
            />
          </Field>
          <Field label="Status">
            <Select value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))} data-testid="filter-status">
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Retired">Retired</option>
            </Select>
          </Field>
          <Field label="Search">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400 dark:text-slate-500 dark:text-slate-500" />
              <Input className="pl-8" value={filters.q} onChange={e=>setFilters(f=>({...f,q:e.target.value}))} placeholder="ID, serial, name, category, location…" data-testid="filter-search" />
            </div>
          </Field>
          <div className="flex items-end">
            <Btn variant="secondary" onClick={()=>setFilters({ ...ASSET_FILTER_DEFAULTS })} data-testid="reset-filters-btn">Reset</Btn>
          </div>
        </div>
      </Card>

      {Object.keys(expiringParams).length > 0 && (
        <Card className="p-3 bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800/50 flex items-center justify-between" data-testid="expiring-filter-banner">
          <div className="text-sm text-amber-900 dark:text-amber-200 dark:text-amber-200">
            Filtering by:&nbsp;
            {expiringParams.expiring_warranty_days != null && `Warranty expiring within ${expiringParams.expiring_warranty_days} days`}
            {expiringParams.expiring_amc_days != null && `AMC expiring within ${expiringParams.expiring_amc_days} days`}
            {expiringParams.expiring_service_days != null && `Service due within ${expiringParams.expiring_service_days} days`}
            {expiringParams.expiring_insurance_days != null && `Vehicle insurance expiring within ${expiringParams.expiring_insurance_days} days`}
            {expiringParams.expired && `Already expired: ${({ warranty: "warranty", amc: "AMC", service: "service due", insurance: "vehicle insurance" })[expiringParams.expired] || expiringParams.expired}`}
            {expiringParams.overdue_service && "Overdue service"}
          </div>
          <Link to="/assets" className="text-sm text-amber-900 dark:text-amber-200 dark:text-amber-200 underline" data-testid="expiring-filter-clear">Clear</Link>
        </Card>
      )}

      {isAdminUser && selected.size > 0 && (
        <Card className="p-3 bg-indigo-50/60 border border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800/50 dark:bg-indigo-900/20 dark:border-indigo-800/50 flex items-center justify-between" data-testid="bulk-toolbar">
          <div className="text-sm font-medium text-indigo-900 dark:text-indigo-200 dark:text-indigo-200" data-testid="bulk-selected-count">{selected.size} selected</div>
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={()=>setSelected(new Set())} data-testid="bulk-clear-btn">Clear</Btn>
            <Btn onClick={()=>setShowMove(true)} data-testid="bulk-move-btn">Move to…</Btn>
          </div>
        </Card>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left">
            <tr>
              {isAdminUser && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox"
                    checked={items.length > 0 && selected.size === items.length}
                    onChange={toggleAll}
                    data-testid="select-all-checkbox" />
                </th>
              )}
              <th className="px-4 py-2 whitespace-nowrap">Asset ID</th><th className="pr-4 whitespace-nowrap">Serial No.</th><th>Name</th><th>Category</th><th>Location</th>{!contextCols && <><th>Area</th>{showTimerCol && <th className="pr-3 whitespace-nowrap">Timer Schedule</th>}<th>Department</th><th>User</th></>}<th>Status</th><th>Custodian</th>{contextCols && contextCols.map(c => <th key={c.field}>{c.label}</th>)}<th></th>
            </tr>
          </thead>
          <tbody data-testid="assets-table-body">
            {items.map(a => (
              <tr key={a.id} className="table-row" data-testid={`asset-row-${a.asset_id}`}>
                {isAdminUser && (
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(a.id)} onChange={()=>toggle(a.id)} data-testid={`select-asset-${a.asset_id}`} />
                  </td>
                )}
                <td className="px-4 py-2 whitespace-nowrap"><span className="font-mono text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">{a.asset_id}</span></td>
                <td className="pr-4 whitespace-nowrap">
                  {a.category_fields?.serial_no
                    ? <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{a.category_fields.serial_no}</span>
                    : <span className="text-slate-400 dark:text-slate-600">—</span>}
                </td>
                <td>{a.name}</td>
                <td><Badge color="indigo">{catName(a.category_code)}</Badge></td>
                <td>{locName(a.location_id)}</td>
                {!contextCols && <>
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{areaName(a.area_id) || (a.category_fields?.area || "—")}</td>
                {showTimerCol && (
                  <td className="pr-3 text-slate-600 dark:text-slate-300 align-top">
                    {a.category_code !== "AIR_CONDITIONER"
                      ? <span className="text-slate-300 dark:text-slate-600">—</span>
                      : fmtTimerSchedule(a)
                        ? <span className="text-xs tabular-nums whitespace-nowrap" title={fmtTimerSchedule(a)}>{fmtTimerSchedule(a)}</span>
                        : <span className="text-xs text-slate-400 dark:text-slate-500">Not Set</span>}
                  </td>
                )}
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{a.department || a.category_fields?.department || "—"}</td>
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{a.user_name || a.category_fields?.user_name || "—"}</td>
                </>}
                <td><Badge color={a.status === "Active" ? "green" : "slate"}>{a.status}</Badge></td>
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{a.custodian || "—"}</td>
                {contextCols && contextCols.map(c => (
                  <td key={c.field} className={"align-top " + (c.due ? ctxDueClass(a.category_fields?.[c.field]) : "text-slate-600 dark:text-slate-300")}>
                    {fmtCtxDate(a.category_fields?.[c.field])}
                  </td>
                ))}
                <td className="text-right pr-3">
                  <Link to={`/assets/${a.id}`} state={{ backTo: listUrl }} onClick={rememberScroll} className="text-indigo-600 hover:underline text-xs" data-testid={`open-asset-${a.asset_id}`}>Open <ChevronRight size={12} className="inline" /></Link>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={(isAdminUser ? 12 : 11) + (contextCols ? contextCols.length - 3 : (showTimerCol ? 1 : 0))} className="text-center py-6 text-slate-500" data-testid="no-assets-msg">No assets match.</td></tr>}
          </tbody>
        </table>
        <div className="flex items-center justify-between p-3 border-t border-slate-100 text-sm">
          <div>Showing {items.length} of {total}</div>
          <div className="flex gap-2">
            <Btn variant="secondary" disabled={page<=1} onClick={()=>setPage(p=>p-1)} data-testid="page-prev">Prev</Btn>
            <span className="px-2 py-1">Page {page}</span>
            <Btn variant="secondary" disabled={items.length<50} onClick={()=>setPage(p=>p+1)} data-testid="page-next">Next</Btn>
          </div>
        </div>
      </Card>

      {showCreate && <AssetCreateModal categories={categories} locations={locations} onClose={()=>setShowCreate(false)} onCreated={()=>{setShowCreate(false); load();}} />}
      {showMove && <BulkMoveModal selectedIds={Array.from(selected)} locations={locations} onClose={()=>setShowMove(false)} onMoved={()=>{setShowMove(false); load();}} />}
    </div>
  );
}

function BulkMoveModal({ selectedIds, locations, onClose, onMoved }) {
  const [target, setTarget] = useState(locations[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.bulkMove(selectedIds, target);
      alert(`Moved ${data.modified} asset(s) to ${data.target_location}.`);
      onMoved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Move ${selectedIds.length} asset(s)`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="bulk-move-form">
        <Field label="Target location">
          <Select required value={target} onChange={e=>setTarget(e.target.value)} data-testid="bulk-move-target">
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </Field>
        <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
          This will update the location_id for {selectedIds.length} asset(s). Maintenance history stays with each asset.
        </p>
        {err && <div className="text-sm text-rose-600" data-testid="bulk-move-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="bulk-move-submit">{busy ? "Moving…" : "Move"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ---------------- Asset create / edit ----------------
function AssetCreateModal({ categories, locations, onClose, onCreated, initial = null }) {
  const user = getUser();
  const [form, setForm] = useState(initial ? {
    ...initial,
    area_id: initial.area_id || "",
    department: initial.department || "",
    user_name: initial.user_name || "",
    custodian: initial.custodian || "",
    dop: initial.dop?.split("T")[0] || "",
    warranty_expiry: initial.warranty_expiry?.split("T")[0] || "",
    amc_expiry: initial.amc_expiry?.split("T")[0] || "",
    last_service_date: initial.last_service_date?.split("T")[0] || "",
    next_service_due: initial.next_service_due?.split("T")[0] || "",
    category_fields: initial.category_fields || {},
  } : {
    category_code: categories[0]?.code || "",
    location_id: user?.role === "location_editor" ? user.location_id : (locations[0]?.id || ""),
    area_id: "",
    name: "", status: "Active", custodian: "", department: "", user_name: "",
    supplier_name: "", dop: "", warranty_expiry: "", amc_expiry: "",
    last_service_date: "", next_service_due: "", notes: "", category_fields: {},
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [areas, setAreas] = useState([]);
  const cat = categories.find(c => c.code === form.category_code);
  const availableLocations = user?.role === "location_editor"
    ? locations.filter(l => l.id === user.location_id)
    : locations;

  // Cascading: refresh area options whenever location changes.
  useEffect(() => {
    if (!form.location_id) { setAreas([]); return; }
    api.listAreas({ location_id: form.location_id })
      .then(r => setAreas(r.data))
      .catch(()=>setAreas([]));
  }, [form.location_id]);
  // Clear area_id if it no longer belongs to the selected location.
  useEffect(() => {
    if (form.area_id && areas.length && !areas.find(a => a.id === form.area_id)) {
      setForm(f => ({ ...f, area_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = { ...form };
      ["dop","warranty_expiry","amc_expiry","last_service_date","next_service_due"].forEach(k => {
        if (!body[k]) delete body[k];
      });
      // strip server-managed fields when editing
      if (initial) {
        delete body.id; delete body.asset_id; delete body.category_code;
        delete body.created_at; delete body.updated_at; delete body.created_by; delete body.updated_by;
        await api.updateAsset(initial.id, body);
      } else {
        await api.createAsset(body);
      }
      onCreated();
    } catch (e2) { setErr(e2.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={initial ? "Edit Asset" : "New Asset"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="asset-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={form.category_code} onChange={e=>setForm(f=>({...f,category_code:e.target.value, category_fields:{}}))} disabled={!!initial} data-testid="asset-category-select">
              {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Location">
            <Select value={form.location_id} onChange={e=>setForm(f=>({...f,location_id:e.target.value, area_id:""}))} data-testid="asset-location-select">
              {availableLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label="Area">
            <Select
              value={form.area_id || ""}
              disabled={!form.location_id || areas.length === 0}
              onChange={e=>setForm(f=>({...f,area_id:e.target.value}))}
              data-testid="asset-area-select"
            >
              <option value="">{areas.length ? "— None —" : "No areas defined for this location"}</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </Field>
          <Field label="Department"><Input value={form.department || ""} onChange={e=>setForm(f=>({...f,department:e.target.value}))} data-testid="asset-department-input" /></Field>
          <Field label="User Name"><Input value={form.user_name || ""} onChange={e=>setForm(f=>({...f,user_name:e.target.value}))} data-testid="asset-user-name-input" /></Field>
          <Field label="Custodian"><Input value={form.custodian || ""} onChange={e=>setForm(f=>({...f,custodian:e.target.value}))} data-testid="asset-custodian-input" /></Field>
          <Field label="Name / Title"><Input required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} data-testid="asset-name-input" /></Field>
          <Field label="Status">
            <Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} data-testid="asset-status-select">
              <option>Active</option><option>Inactive</option><option>Retired</option>
            </Select>
          </Field>
          <Field label="Supplier"><Input value={form.supplier_name || ""} onChange={e=>setForm(f=>({...f,supplier_name:e.target.value}))} /></Field>
          <Field label="Date of Purchase"><Input type="date" value={form.dop || ""} onChange={e=>setForm(f=>({...f,dop:e.target.value}))} /></Field>
          <Field label="Warranty Expiry"><Input type="date" value={form.warranty_expiry || ""} onChange={e=>setForm(f=>({...f,warranty_expiry:e.target.value}))} /></Field>
          <Field label="AMC Expiry"><Input type="date" value={form.amc_expiry || ""} onChange={e=>setForm(f=>({...f,amc_expiry:e.target.value}))} /></Field>
          <Field label="Last Service Date"><Input type="date" value={form.last_service_date || ""} onChange={e=>setForm(f=>({...f,last_service_date:e.target.value}))} /></Field>
          <Field label="Next Service Due"><Input type="date" value={form.next_service_due || ""} onChange={e=>setForm(f=>({...f,next_service_due:e.target.value}))} /></Field>
        </div>
        {cat && cat.extra_fields?.length > 0 && (
          <div>
            <div className="text-sm font-semibold mb-2 text-slate-700">{cat.name} specific fields</div>
            <div className="grid grid-cols-2 gap-3">
              {cat.extra_fields.map(f => {
                const raw = form.category_fields?.[f.name];
                const val = (f.type === "date" && typeof raw === "string" && raw.includes("T")) ? raw.split("T")[0] : (raw ?? "");
                return (
                  <Field key={f.name} label={f.label + (f.required ? " *" : "")}>
                    <Input
                      type={f.type === "date" ? "date" : (f.type === "number" ? "number" : "text")}
                      required={!!f.required}
                      value={val}
                      onChange={e => setForm(s => ({ ...s, category_fields: { ...s.category_fields, [f.name]: e.target.value } }))}
                      data-testid={`asset-extra-${f.name}`}
                    />
                  </Field>
                );
              })}
            </div>
          </div>
        )}
        {form.category_code === "AIR_CONDITIONER" && (
          <TimerScheduleEditor
            periods={form.category_fields?.timer_schedule || []}
            onChange={(next) => setForm(s => ({ ...s, category_fields: { ...s.category_fields, timer_schedule: next } }))}
          />
        )}
        <Field label="Notes"><Input value={form.notes || ""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} /></Field>
        {err && <div className="text-sm text-rose-600 bg-rose-50 px-3 py-2 rounded" data-testid="asset-form-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose} data-testid="asset-form-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="asset-form-save">{busy ? "Saving…" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

/** Add / edit / remove the ON–OFF periods an AC runs on. */
function TimerScheduleEditor({ periods, onChange }) {
  const rows = Array.isArray(periods) ? periods : [];
  const setRow = (i, key, value) =>
    onChange(rows.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  const addRow = () => onChange([...rows, { on: "", off: "" }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div data-testid="timer-schedule-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Timer schedule</div>
        <Btn variant="secondary" onClick={addRow} data-testid="timer-add-period">
          <Plus size={13} className="inline mr-1" />Add period
        </Btn>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5"
             data-testid="timer-empty">
          No timer set — this AC shows “Not Set” in the assets list. Add a period to schedule ON/OFF times.
        </div>
      ) : (
        <div className="space-y-2" data-testid="timer-rows">
          {rows.map((p, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`timer-row-${i}`}>
              <Field label={i === 0 ? "ON time" : ""}>
                <Input type="time" value={p.on || ""} onChange={e => setRow(i, "on", e.target.value)}
                       data-testid={`timer-on-${i}`} />
              </Field>
              <span className="pb-2.5 text-slate-400">–</span>
              <Field label={i === 0 ? "OFF time" : ""}>
                <Input type="time" value={p.off || ""} onChange={e => setRow(i, "off", e.target.value)}
                       data-testid={`timer-off-${i}`} />
              </Field>
              <span className="pb-2 text-xs text-slate-500 dark:text-slate-400 flex-1 truncate">
                {p.on && p.off
                  ? <>{fmtTime12(p.on)}–{fmtTime12(p.off)}{p.off < p.on && <span className="text-amber-600 dark:text-amber-400"> (overnight)</span>}</>
                  : "set both times"}
              </span>
              <button type="button" onClick={() => removeRow(i)}
                      className="mb-1 text-rose-500 hover:text-rose-700 transition p-1.5 rounded hover:bg-rose-50 dark:hover:bg-slate-800"
                      title="Remove this period" aria-label={`Remove timer period ${i + 1}`}
                      data-testid={`timer-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Periods are saved in chronological order. An OFF time before its ON time runs overnight.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------- Asset detail ----------------
function AssetDetail() {
  const { id } = useParams();
  const user = getUser();
  const nav = useNavigate();
  const route = useRouteLoc();
  // Return to the exact list view we came from (filters, search, page). Falls back
  // to the last list view visited this session for QR-scan / dashboard entries.
  const backToAssets = useMemo(() => {
    if (route.state?.backTo) return route.state.backTo;
    try {
      const saved = JSON.parse(sessionStorage.getItem(ASSET_SCROLL_KEY) || "null");
      if (saved?.url?.startsWith("/assets")) return saved.url;
    } catch { /* ignore */ }
    return "/assets";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.state]);
  const [asset, setAsset] = useState(null);
  const [logs, setLogs] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddLog, setShowAddLog] = useState(false);
  const [editLog, setEditLog] = useState(null);
  const [showAddCond, setShowAddCond] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [insurance, setInsurance] = useState([]);
  const [showAddIns, setShowAddIns] = useState(false);
  const [editIns, setEditIns] = useState(null);

  const reload = () => {
    api.getAsset(id).then(r => setAsset(r.data));
    api.listMaintenance(id).then(r => setLogs(r.data));
    api.listConditions(id).then(r => setConditions(r.data)).catch(()=>setConditions([]));
    api.listInsurance(id).then(r => setInsurance(r.data)).catch(()=>setInsurance([]));
  };
  useEffect(() => {
    reload();
    api.listCategories().then(r => setCategories(r.data));
    api.listLocations().then(r => setLocations(r.data));
    api.listAreas().then(r => setAreas(r.data)).catch(()=>setAreas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!asset) return <div className="p-8">Loading…</div>;
  const cat = categories.find(c => c.code === asset.category_code);
  const totalSpend = (logs || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const loc = locations.find(l => l.id === asset.location_id);
  const areaName = areas.find(a => a.id === asset.area_id)?.name
    || asset.category_fields?.area
    || "";
  const canEdit = canEditAtLocation(user, asset.location_id);
  const isVehicle = asset.category_code === "VEHICLE";
  // Latest odometer reading, for the history-card header badge
  const kmsReadings = logs
    .filter(l => l.kms != null)
    .map(l => ({ d: l.date_of_service || "", kms: Number(l.kms) }));
  const latestKms = kmsReadings
    .slice()
    .sort((a, b) => b.d.localeCompare(a.d))[0]?.kms ?? null;

  const onDelete = async () => {
    if (!window.confirm(`Delete ${asset.asset_id}? This also deletes its maintenance & condition history.`)) return;
    await api.deleteAsset(asset.id);
    nav(backToAssets);
  };

  const deleteLog = async (log) => {
    const summary = `${fmtDMY(log.date_of_service)} · ${log.service_done_by || "no vendor"} · ₹${log.amount || 0}`;
    if (!window.confirm(`Delete this maintenance log?\n\n${summary}\n\nThis cannot be undone.`)) return;
    try {
      await api.deleteMaintenance(log.id);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete log");
    }
  };

  // Remove a single file from a log — for the wrong file, or the same one twice.
  const deleteLogAttachment = async (att, log) => {
    // A log is created with proof attached; don't let it end up with none.
    if ((log?.attachments || []).length <= 1) {
      alert("This is the log's only attachment.\n\nUse Edit log to upload the correct file first, then delete this one — or delete the whole log.");
      return;
    }
    if (!window.confirm(`Remove attachment "${att.filename}" from this log?\n\nThis cannot be undone.`)) return;
    try {
      await api.deleteAttachment(att.id);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete attachment");
    }
  };

  const onExportMaintenance = async () => {
    try {
      const { data } = await api.exportMaintenance({ asset_id: asset.id });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maintenance-${asset.asset_id}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed. Please try again.");
    }
  };

  const deleteInsurance = async (rec) => {
    const summary = `${fmtDMY(rec.insurance_from)} → ${fmtDMY(rec.insurance_to)} · ${rec.insurance_company} · ${rec.policy_number}`;
    if (!window.confirm(`Delete this insurance record?\n\n${summary}\n\nThis also removes its attachments and cannot be undone.`)) return;
    try {
      await api.deleteInsurance(rec.id);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete insurance record");
    }
  };

  const deleteInsAttachment = async (att, rec) => {
    if ((rec?.attachments || []).length <= 1) {
      alert("This is the record's only document.\n\nUse Edit to upload the correct policy first, then delete this one — or delete the whole record.");
      return;
    }
    if (!window.confirm(`Remove attachment "${att.filename}" from this insurance record?\n\nThis cannot be undone.`)) return;
    try {
      await api.deleteAttachment(att.id);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete attachment");
    }
  };

  const onExportInsurance = async () => {
    try {
      const { data } = await api.exportInsurance(asset.id);
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `insurance-${asset.asset_id}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed. Please try again.");
    }
  };

  const totalInsurance = (insurance || []).reduce((s, r) => s + (Number(r.insurance_amount) || 0), 0);
  // Newest policy drives the asset-level banner.
  const currentPolicy = (insurance || [])[0] || null;

  return (
    <div className="p-8 space-y-6" data-testid="asset-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Package size={18} />
          </div>
          <div>
            <Link to={backToAssets} className="text-xs text-indigo-600 hover:underline" data-testid="back-to-assets">← Back to assets</Link>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100" data-testid="asset-title">{asset.asset_id} <span className="text-slate-400 dark:text-slate-500 dark:text-slate-500 font-normal">·</span> {asset.name}</h1>
            <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 mt-1">
              <Badge color="indigo">{cat?.name || asset.category_code}</Badge>
              <span className="ml-2">{loc?.name || "—"}</span>
              <span className="ml-2"><Badge color={asset.status === "Active" ? "green" : "slate"}>{asset.status}</Badge></span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Btn variant="secondary" onClick={()=>setShowQr(true)} data-testid="show-qr-btn">Show QR</Btn>
          {canEdit && <Btn variant="secondary" onClick={()=>setShowEdit(true)} data-testid="edit-asset-btn"><Edit3 size={14} className="inline mr-1" />Edit</Btn>}
          {isAdmin() && <Btn variant="danger" onClick={onDelete} data-testid="delete-asset-btn"><Trash2 size={14} className="inline mr-1" />Delete</Btn>}
        </div>
      </div>

      {isVehicle && currentPolicy && currentPolicy.status !== "Active" && (
        <Card
          className={"p-3 flex items-center gap-2 text-sm " + (currentPolicy.status === "Expired"
            ? "bg-rose-50 border border-rose-200 text-rose-800 dark:bg-rose-900/20 dark:border-rose-800/50 dark:text-rose-200"
            : "bg-amber-50 border border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-200")}
          data-testid="insurance-warning"
        >
          <AlertTriangle size={15} className="flex-shrink-0" />
          <span>
            {currentPolicy.status === "Expired"
              ? <>Insurance <strong>expired</strong> on {fmtDMY(currentPolicy.insurance_to)}</>
              : <>Insurance <strong>expires soon</strong> — {fmtDMY(currentPolicy.insurance_to)}</>}
            {" "}· {currentPolicy.insurance_company} · policy {currentPolicy.policy_number}
          </span>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-6">
        <Card className="p-5 col-span-1">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Details</h2>
          <DetailRow label="Area" value={areaName} testid="asset-detail-area" />
          <DetailRow label="Department" value={asset.department || asset.category_fields?.department} testid="asset-detail-department" />
          <DetailRow label="User Name" value={asset.user_name || asset.category_fields?.user_name} testid="asset-detail-user-name" />
          <DetailRow label="Custodian" value={asset.custodian} testid="asset-detail-custodian" />
          <DetailRow label="Supplier" value={asset.supplier_name} />
          <DetailRow label="Date of Purchase" value={fmtDMY(asset.dop)} />
          <DetailRow label="Warranty Expiry" value={fmtDMY(asset.warranty_expiry)} />
          <DetailRow label="AMC Expiry" value={fmtDMY(asset.amc_expiry)} />
          <DetailRow label="Last Service" value={fmtDMY(asset.last_service_date)} />
          <DetailRow label="Next Service Due" value={fmtDMY(asset.next_service_due)} />
          <DetailRow label="Total maintenance" value={totalSpend > 0 ? `₹${totalSpend.toLocaleString("en-IN")}` : "—"} testid="asset-detail-total-spend" />
          {asset.notes && <DetailRow label="Notes" value={asset.notes} />}
        </Card>
        <Card className="p-5 col-span-2">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">{cat?.name} fields</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {(cat?.extra_fields || []).map(f => (
              <DetailRow key={f.name} label={f.label} value={fmtField(asset.category_fields?.[f.name])} />
            ))}
          </div>
          {asset.category_code === "AIR_CONDITIONER" && (
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800" data-testid="asset-detail-timer">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Timer schedule</div>
              {timerPeriods(asset).length === 0 ? (
                <span className="text-sm text-slate-400 dark:text-slate-500">Not Set</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {timerPeriods(asset).map((p, i) => (
                    <span key={i} className="pill-sky tabular-nums" data-testid={`asset-timer-period-${i}`}>
                      {fmtTime12(p.on)} – {fmtTime12(p.off)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      <AssetAttachments assetId={asset.id} canEdit={canEdit} />

      <Card className="p-5" data-testid="maintenance-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2">
            <Wrench size={14} className="text-emerald-500" />
            Maintenance history
            <span className="text-slate-400 dark:text-slate-500 font-normal">({logs.length})</span>
            {totalSpend > 0 && (
              <span className="pill-slate ml-1" data-testid="total-spend-pill">
                ₹{totalSpend.toLocaleString("en-IN")} total
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <Btn variant="secondary" onClick={onExportMaintenance} data-testid="export-maintenance-btn">
              <Download size={14} className="inline mr-1.5" />
              Export
            </Btn>
            {canEdit && <Btn onClick={()=>setShowAddLog(true)} data-testid="add-maintenance-btn"><Plus size={14} className="inline mr-1" />Add Log</Btn>}
          </div>
        </div>
        {logs.length === 0 ? <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">No service records yet.</div> :
        <table className="w-full text-sm">
          <thead className="table-head text-left">
            <tr className="divide-x divide-slate-200 dark:divide-slate-800"><th className="px-2 py-1">Date</th><th>KMS</th><th>Service By</th><th>Amount</th><th>Details</th><th>Parts</th><th>Remarks</th><th>Attachments</th><th>By</th><th></th></tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="table-row divide-x divide-slate-100 dark:divide-slate-800" data-testid={`mlog-row-${l.id}`}>
                <td className="px-2 py-2 align-top">{fmtDMY(l.date_of_service)}</td>
                <td className="align-top tabular-nums">{l.kms != null ? Number(l.kms).toLocaleString() : <span className="text-slate-400">—</span>}</td>
                <td className="align-top">{l.service_done_by}</td>
                <td className="align-top">{l.amount ? `₹${l.amount}` : "—"}</td>
                <td className="align-top">{l.service_details}</td>
                <td className="align-top">{l.parts_changed}</td>
                <td className="align-top">{l.remarks}</td>
                <td className="align-top">
                  {(l.attachments || []).length === 0 ? <span className="text-slate-400 dark:text-slate-500 dark:text-slate-500 text-xs">—</span> :
                    <ul className="space-y-1" data-testid={`mlog-attachments-${l.id}`}>
                      {l.attachments.map(att => (
                        <li key={att.id} className="flex items-start gap-1 group">
                          <button type="button" onClick={() => downloadAttachment(att)}
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline text-left"
                            data-testid={`download-attachment-${att.id}`}
                            title={`${att.filename} · ${(att.size_bytes/1024).toFixed(1)} KB`}>
                            <Paperclip size={11} className="flex-shrink-0 mt-0.5" /> {att.filename}
                          </button>
                          {canEdit && (
                            <button type="button" onClick={() => deleteLogAttachment(att, l)}
                              className="text-slate-300 dark:text-slate-600 hover:text-rose-600 dark:hover:text-rose-400 transition p-0.5 rounded flex-shrink-0"
                              title={`Delete ${att.filename}`}
                              aria-label={`Delete attachment ${att.filename}`}
                              data-testid={`delete-attachment-${att.id}`}>
                              <X size={11} />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  }
                </td>
                <td className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 align-top">{l.created_by_username}</td>
                <td className="text-right pr-2 align-top">
                  <div className="flex items-center justify-end gap-1">
                    {canEdit && (
                      <button
                        onClick={() => setEditLog(l)}
                        className="text-slate-400 hover:text-indigo-600 transition p-1 rounded hover:bg-indigo-50 dark:hover:bg-slate-800"
                        title="Edit log"
                        aria-label="Edit maintenance log"
                        data-testid={`edit-log-${l.id}`}
                      >
                        <Edit3 size={14} />
                      </button>
                    )}
                    {isAdmin() && (
                      <button
                        onClick={() => deleteLog(l)}
                        className="text-rose-500 hover:text-rose-700 transition p-1 rounded hover:bg-rose-50"
                        title="Delete log"
                        aria-label="Delete maintenance log"
                        data-testid={`delete-log-${l.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </Card>

      {isVehicle && (
        <Card className="p-5" data-testid="insurance-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <ShieldCheck size={14} className="text-sky-500" />
              Insurance history
              <span className="text-slate-400 dark:text-slate-500 font-normal">({insurance.length})</span>
              {totalInsurance > 0 && (
                <span className="pill-slate ml-1" data-testid="total-insurance-pill">
                  ₹{totalInsurance.toLocaleString("en-IN")} total
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <Btn variant="secondary" onClick={onExportInsurance} data-testid="export-insurance-btn">
                <Download size={14} className="inline mr-1.5" />
                Export
              </Btn>
              {canEdit && <Btn onClick={()=>setShowAddIns(true)} data-testid="add-insurance-btn"><Plus size={14} className="inline mr-1" />Add Insurance</Btn>}
            </div>
          </div>
          {insurance.length === 0 ? <div className="text-sm text-slate-500 dark:text-slate-400">No insurance records yet.</div> :
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-head text-left">
              <tr className="divide-x divide-slate-200 dark:divide-slate-800">
                <th className="px-2 py-1 whitespace-nowrap">From Date</th>
                <th className="px-2 whitespace-nowrap">To Date</th>
                <th className="px-2">Insurance Company</th>
                <th className="px-2 whitespace-nowrap">Policy Number</th>
                <th className="px-2 text-right whitespace-nowrap">Amount</th>
                <th className="px-2 text-right whitespace-nowrap" title="Insured Declared Value">IDV</th>
                <th className="px-2 text-right whitespace-nowrap">Premium</th>
                <th className="px-2 whitespace-nowrap">Type</th>
                <th className="px-2 whitespace-nowrap">Policy Doc</th>
                <th className="px-2">Remarks</th>
                <th className="px-2 whitespace-nowrap">Status</th>
                <th className="px-2 whitespace-nowrap">By</th><th></th>
              </tr>
            </thead>
            <tbody>
              {insurance.map(r => (
                <tr key={r.id} className="table-row divide-x divide-slate-100 dark:divide-slate-800" data-testid={`ins-row-${r.id}`}>
                  <td className="px-2 py-2 align-top whitespace-nowrap">{fmtDMY(r.insurance_from)}</td>
                  <td className={"px-2 align-top whitespace-nowrap " + insToneClass(r.status)}>{fmtDMY(r.insurance_to)}</td>
                  <td className="px-2 align-top">{r.insurance_company}</td>
                  <td className="px-2 align-top font-mono text-xs whitespace-nowrap">{r.policy_number}</td>
                  <td className="px-2 align-top text-right tabular-nums whitespace-nowrap font-medium">
                    ₹{Number(r.insurance_amount || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-2 align-top text-right tabular-nums whitespace-nowrap">
                    {r.idv > 0 ? `₹${Number(r.idv).toLocaleString("en-IN")}` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 align-top text-right tabular-nums whitespace-nowrap">
                    {r.premium_amount > 0 ? `₹${Number(r.premium_amount).toLocaleString("en-IN")}` : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-2 align-top whitespace-nowrap text-slate-600 dark:text-slate-300">{r.insurance_type || "—"}</td>
                  {/* Policy files get a fixed, narrow column — long scheme names
                      would otherwise push every other column off screen. */}
                  <td className="px-2 align-top">
                    {(r.attachments || []).length === 0 ? <span className="text-slate-400 text-xs">—</span> :
                      <ul className="space-y-1 w-[7.5rem]" data-testid={`ins-attachments-${r.id}`}>
                        {r.attachments.map(att => (
                          <li key={att.id} className="flex items-center gap-1 min-w-0">
                            <button type="button" onClick={() => downloadAttachment(att)}
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline min-w-0"
                              data-testid={`download-attachment-${att.id}`}
                              title={`${att.filename} · ${(att.size_bytes/1024).toFixed(1)} KB — click to download`}>
                              <Paperclip size={11} className="flex-shrink-0" />
                              <span className="truncate">{att.filename}</span>
                            </button>
                            {canEdit && (
                              <button type="button" onClick={() => deleteInsAttachment(att, r)}
                                className="text-slate-300 dark:text-slate-600 hover:text-rose-600 dark:hover:text-rose-400 transition p-0.5 rounded flex-shrink-0"
                                title={`Delete ${att.filename}`}
                                aria-label={`Delete attachment ${att.filename}`}
                                data-testid={`delete-attachment-${att.id}`}>
                                <X size={11} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    }
                  </td>
                  <td className="px-2 align-top text-slate-600 dark:text-slate-300 max-w-[10rem] truncate" title={r.remarks || ""}>
                    {r.remarks || "—"}
                  </td>
                  <td className="px-2 align-top whitespace-nowrap"><Badge color={insBadgeColor(r.status)}>{r.status}</Badge></td>
                  <td className="px-2 text-xs text-slate-500 dark:text-slate-400 align-top whitespace-nowrap" title={insAuditTitle(r)}>
                    {r.created_by_username}
                  </td>
                  <td className="text-right pr-2 align-top">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <button
                          onClick={() => setEditIns(r)}
                          className="text-slate-400 hover:text-indigo-600 transition p-1 rounded hover:bg-indigo-50 dark:hover:bg-slate-800"
                          title="Edit insurance record"
                          aria-label="Edit insurance record"
                          data-testid={`edit-ins-${r.id}`}
                        >
                          <Edit3 size={14} />
                        </button>
                      )}
                      {isAdmin() && (
                        <button
                          onClick={() => deleteInsurance(r)}
                          className="text-rose-500 hover:text-rose-700 transition p-1 rounded hover:bg-rose-50"
                          title="Delete insurance record"
                          aria-label="Delete insurance record"
                          data-testid={`delete-ins-${r.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>}
        </Card>
      )}

      {cat?.has_condition_snapshot && (
        <Card className="p-5" data-testid="condition-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2"><Activity size={14} /> Condition snapshots ({conditions.length})</h2>
            {canEdit && <Btn onClick={()=>setShowAddCond(true)} data-testid="add-condition-btn"><Plus size={14} className="inline mr-1" />Add Snapshot</Btn>}
          </div>
          {conditions.length === 0 ? <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">No condition snapshots yet.</div> :
          <table className="w-full text-sm">
            <thead className="table-head text-left">
              <tr><th className="px-2 py-1">Date</th><th>Cracks</th><th>Water Circ.</th><th>Pump</th><th>Fan</th><th>Motor</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {conditions.map(c => (
                <tr key={c.id} className="table-row">
                  <td className="px-2 py-2">{fmtDMY(c.snapshot_date)}</td>
                  <td><Badge color={c.cracks_in_body ? "red" : "green"}>{c.cracks_in_body ? "Yes" : "No"}</Badge></td>
                  <td><Badge color={c.water_circulation_ok ? "green" : "red"}>{c.water_circulation_ok ? "OK" : "NO"}</Badge></td>
                  <td><Badge color={c.pump_working ? "green" : "red"}>{c.pump_working ? "OK" : "NO"}</Badge></td>
                  <td><Badge color={c.fan_working ? "green" : "red"}>{c.fan_working ? "OK" : "NO"}</Badge></td>
                  <td><Badge color={c.motor_working ? "green" : "red"}>{c.motor_working ? "OK" : "NO"}</Badge></td>
                  <td className="text-xs">{c.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>}
        </Card>
      )}

      {showEdit && <AssetCreateModal categories={categories} locations={locations} initial={asset} onClose={()=>setShowEdit(false)} onCreated={()=>{setShowEdit(false); reload();}} />}
      {showAddLog && <MaintenanceModal assetId={id} isVehicle={isVehicle} priorLogs={logs} onClose={()=>setShowAddLog(false)} onSaved={()=>{setShowAddLog(false); reload();}} />}
      {editLog && <MaintenanceModal assetId={id} isVehicle={isVehicle} priorLogs={logs} existing={editLog} onClose={()=>setEditLog(null)} onSaved={()=>{setEditLog(null); reload();}} />}
      {showAddCond && <ConditionModal assetId={id} onClose={()=>setShowAddCond(false)} onSaved={()=>{setShowAddCond(false); reload();}} />}
      {showAddIns && <InsuranceModal assetId={id} onClose={()=>setShowAddIns(false)} onSaved={()=>{setShowAddIns(false); reload();}} />}
      {editIns && <InsuranceModal assetId={id} existing={editIns} onClose={()=>setEditIns(null)} onSaved={()=>{setEditIns(null); reload();}} />}
      {showQr && (() => {
        const locName = locations.find(l => l.id === asset.location_id)?.name || "—";
        const areaName = areas.find(a => a.id === asset.area_id)?.name || "";
        const dept = asset.department || asset.category_fields?.department || "";
        const userName = asset.user_name || asset.category_fields?.user_name || "";
        const custodian = asset.custodian || "";
        const who = custodian || userName || "—";
        const fromParts = [locName, areaName, dept].filter(Boolean);
        const fromLine = fromParts.join(" · ");
        return (
          <Modal title={`QR — ${asset.asset_id}`} onClose={() => setShowQr(false)}>
            <div className="space-y-4" data-testid="qr-modal">
              <img
                src={api.qrPngUrl(asset.id)}
                alt={`QR for ${asset.asset_id}`}
                className="mx-auto w-64 h-64 border border-slate-200 dark:border-slate-700 rounded bg-white"
                data-testid="qr-image"
              />
              <div className="mx-auto w-full max-w-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-3 space-y-2"
                   data-testid="qr-asset-details">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300">Asset ID</span>
                  <span className="text-sm font-mono font-bold text-slate-900 dark:text-slate-50" data-testid="qr-detail-id">{asset.asset_id}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300">Name</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-50 text-right break-words" data-testid="qr-detail-name">{asset.name || "—"}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300 flex-shrink-0">From</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-50 text-right break-words" data-testid="qr-detail-from">{fromLine || "—"}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 dark:text-slate-300 flex-shrink-0">User</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-50 text-right break-words" data-testid="qr-detail-user">{who}</span>
                </div>
              </div>
              <div className="text-center text-sm text-slate-600 dark:text-slate-300 dark:text-slate-300">
                Scan with any phone camera to open this asset's page.
              </div>
              <div className="text-center text-xs text-slate-400 dark:text-slate-500 dark:text-slate-500 font-mono break-all" data-testid="qr-url">
                {window.location.origin}/scan/{asset.asset_id}
              </div>
              <div className="text-center">
                <Btn variant="secondary" onClick={() => {
                  const a = document.createElement("a");
                  a.href = api.qrPngUrl(asset.id);
                  a.download = `${asset.asset_id}.png`;
                  a.click();
                }} data-testid="qr-download-btn">Download PNG</Btn>
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}
const DetailRow = ({ label, value, testid }) => (
  <div className="flex justify-between py-1 text-sm border-b border-slate-50 dark:border-slate-800 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium text-right max-w-[60%] break-words" data-testid={testid}>{value || "—"}</span>
  </div>
);
// ---------------- AC timer schedule ----------------
// Stored on the asset as category_fields.timer_schedule: [{on:"09:00", off:"13:00"}, …]
const fmtTime12 = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ""));
  if (!m) return String(hhmm || "");
  const h = parseInt(m[1], 10);
  return `${String(h % 12 || 12).padStart(2, "0")}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
};
const timerPeriods = (asset) => {
  const raw = asset?.category_fields?.timer_schedule;
  if (!Array.isArray(raw)) return [];
  return raw.filter(p => p && p.on && p.off);
};
const fmtTimerSchedule = (asset) => {
  const periods = timerPeriods(asset);
  if (!periods.length) return "";
  return periods.map(p => `${fmtTime12(p.on)}–${fmtTime12(p.off)}`).join(", ");
};

const fmtDMY = (v) => {
  if (!v) return "";
  const s = String(v).slice(0, 10);
  const p = s.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
};
const fmtField = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && v.match(/^\d{4}-\d{2}-\d{2}/)) return fmtDMY(v);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

async function downloadAttachment(att) {
  try {
    const { data } = await api.downloadAttachment(att.id);
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    alert(e.response?.data?.detail || "Download failed");
  }
}

// ---------------- Asset-level attachments (documents card) ----------------
function AssetAttachments({ assetId, canEdit }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);

  const load = () => api.listAssetAttachments(assetId).then(r => setFiles(r.data)).catch(() => setFiles([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [assetId]);

  const onPick = () => fileInputRef.current?.click();
  const onUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true); setErr("");
    try {
      await api.uploadAssetAttachments(assetId, picked);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const onDelete = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    try { await api.deleteAttachment(f.id); load(); }
    catch (e2) { alert(e2.response?.data?.detail || "Delete failed"); }
  };

  const kindFor = (ct, fn) => {
    const c = (ct || "").toLowerCase();
    const n = (fn || "").toLowerCase();
    if (c.startsWith("image/")) return "image";
    if (c === "application/pdf" || n.endsWith(".pdf")) return "pdf";
    if (c.includes("word") || /\.(docx?|odt)$/.test(n)) return "doc";
    if (c.includes("sheet") || c.includes("excel") || /\.(xlsx?|csv)$/.test(n)) return "xls";
    return "file";
  };
  const formatSize = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card className="p-5" data-testid="asset-attachments-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2">
          <FileText size={14} className="text-indigo-500" /> Documents
          <span className="text-slate-400 dark:text-slate-500 dark:text-slate-500 font-normal">({files.length})</span>
        </h2>
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onUpload}
              data-testid="asset-attachment-input"
            />
            <Btn variant="secondary" onClick={onPick} disabled={uploading} data-testid="asset-attachment-upload">
              <Upload size={14} className="inline mr-1.5" />
              {uploading ? "Uploading…" : "Upload"}
            </Btn>
          </>
        )}
      </div>
      {err && <div className="text-xs text-rose-700 bg-rose-50 px-3 py-2 rounded mb-3" data-testid="asset-attachment-error">{err}</div>}
      {files.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 py-4" data-testid="asset-attachments-empty">
          No documents attached.
          {canEdit && <span className="text-[11px] text-slate-400 dark:text-slate-500 dark:text-slate-500 dark:text-slate-500 ml-1">Manuals, warranty cards, purchase invoices, photos — 5 MB per file.</span>}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800 dark:divide-slate-800" data-testid="asset-attachments-list">
          {files.map(f => {
            const kind = kindFor(f.content_type, f.filename);
            return (
              <div key={f.id} className="flex items-center justify-between py-2.5" data-testid={`asset-attachment-row-${f.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider">
                    {kind}
                  </div>
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => downloadAttachment(f)}
                      className="text-sm font-medium text-slate-900 dark:text-slate-100 dark:text-slate-100 hover:text-indigo-600 truncate block text-left"
                      data-testid={`asset-attachment-link-${f.id}`}
                      title={f.filename}
                    >
                      {f.filename}
                    </button>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 dark:text-slate-500">
                      {formatSize(f.size_bytes)} · uploaded {fmtDMY(f.uploaded_at)} by {f.uploaded_by_username || "—"}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={() => onDelete(f)}
                    className="text-rose-500 hover:text-rose-700 transition p-1.5 rounded hover:bg-rose-50"
                    title="Delete attachment"
                    aria-label={`Delete ${f.filename}`}
                    data-testid={`asset-attachment-delete-${f.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function MaintenanceModal({ assetId, isVehicle = false, priorLogs = [], existing = null, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState({
    date_of_service: existing?.date_of_service?.split("T")[0] || new Date().toISOString().slice(0, 10),
    service_done_by: existing?.service_done_by || "",
    amount: existing?.amount ?? 0,
    service_details: existing?.service_details || "",
    parts_changed: existing?.parts_changed || "",
    remarks: existing?.remarks || "",
    kms: existing?.kms != null ? String(existing.kms) : "",
  });
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const prevReading = priorLogs
    .filter(l => l.kms != null && l.id !== existing?.id && (l.date_of_service || "") < ((f.date_of_service || "") + "T99"))
    .map(l => ({ d: l.date_of_service || "", kms: Number(l.kms) }))
    .sort((a, b) => b.d.localeCompare(a.d))[0];

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles(picked);
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && files.length === 0) {
      setErr("At least one attachment is required (invoice, receipt, photo, or note).");
      return;
    }
    const oversized = files.find((x) => x.size > 5 * 1024 * 1024);
    if (oversized) {
      setErr(`"${oversized.name}" exceeds 5 MB. Compress it or attach a smaller file.`);
      return;
    }
    const kmsVal = (f.kms === "" || f.kms == null) ? null : parseInt(f.kms, 10);
    if (isVehicle && kmsVal == null) {
      setErr("Odometer reading (kms) is required for vehicles.");
      return;
    }
    const payload = { ...f, kms: kmsVal };

    setBusy(true); setErr("");
    if (isEdit) {
      try {
        await api.updateMaintenance(existing.id, payload);
        if (files.length) await api.uploadMaintenanceAttachments(existing.id, files);
        onSaved();
      } catch (e2) {
        setErr(e2.response?.data?.detail || "Failed to save log. Try again.");
      } finally {
        setBusy(false);
      }
      return;
    }
    let createdLogId = null;
    try {
      const { data: log } = await api.addMaintenance(assetId, payload);
      createdLogId = log.id;
      await api.uploadMaintenanceAttachments(createdLogId, files);
      onSaved();
    } catch (e2) {
      if (createdLogId) {
        try { await api.deleteMaintenance(createdLogId); } catch (_) {}
      }
      setErr(e2.response?.data?.detail || "Failed to save log. Check files and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={isEdit ? "Edit maintenance log" : "Add maintenance log"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="maintenance-form">
        <Field label="Date of service">
          <Input type="date" required value={f.date_of_service}
            onChange={(e) => setF({ ...f, date_of_service: e.target.value })}
            data-testid="maintenance-date" />
        </Field>
        {isVehicle && (
          <Field label="Odometer (kms)">
            <Input type="number" min="0" step="1" required value={f.kms}
              onChange={(e) => setF({ ...f, kms: e.target.value })}
              data-testid="maintenance-kms" placeholder="e.g. 45230" />
            {prevReading && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                Last reading: {prevReading.kms.toLocaleString()} km — new reading must be ≥ this.
              </p>
            )}
          </Field>
        )}
        <Field label="Service done by">
          <Input value={f.service_done_by}
            onChange={(e) => setF({ ...f, service_done_by: e.target.value })}
            data-testid="maintenance-service-by" />
        </Field>
        <Field label="Amount (₹)">
          <Input type="number" step="0.01" value={f.amount}
            onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value) || 0 })}
            data-testid="maintenance-amount" />
        </Field>
        <Field label="Service details">
          <Input value={f.service_details}
            onChange={(e) => setF({ ...f, service_details: e.target.value })}
            data-testid="maintenance-details" />
        </Field>
        <Field label="Parts changed">
          <Input value={f.parts_changed}
            onChange={(e) => setF({ ...f, parts_changed: e.target.value })}
            data-testid="maintenance-parts" />
        </Field>
        <Field label="Remarks">
          <Input value={f.remarks}
            onChange={(e) => setF({ ...f, remarks: e.target.value })}
            data-testid="maintenance-remarks" />
        </Field>

        <Field label={isEdit ? "Add attachment (optional)" : "Attachment (required)"}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onPickFiles}
            className="block w-full text-xs text-slate-600 dark:text-slate-300
                       file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0
                       file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700
                       hover:file:bg-indigo-100 dark:file:bg-slate-700 dark:file:text-slate-200"
            data-testid="maintenance-attachment-input"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
            {isEdit ? "Optionally attach more invoices, photos, or notes. 5 MB per file." : "Upload an invoice, receipt, photo, or note. At least one file required. 5 MB per file."}
          </p>
          {files.length > 0 && (
            <ul className="text-[11px] text-slate-600 dark:text-slate-300 mt-1.5 space-y-0.5"
                data-testid="maintenance-attachment-list">
              {files.map((x, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <Check size={11} className="text-emerald-500" />
                  <span className="truncate">{x.name}</span>
                  <span className="text-slate-400">({(x.size / 1024).toFixed(1)} KB)</span>
                </li>
              ))}
            </ul>
          )}
        </Field>

        {err && (
          <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 ring-1 ring-rose-100 dark:ring-rose-900/50 px-3 py-2 rounded-lg"
               data-testid="maintenance-error">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy || (!isEdit && files.length === 0)} data-testid="maintenance-submit">
            {busy ? "Saving…" : "Save log"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
// ---------------- Insurance history (vehicles) ----------------
const INSURANCE_TYPES = ["Comprehensive", "Third Party", "Other"];
const insBadgeColor = (s) => ({ Active: "green", "Expiring Soon": "amber", Expired: "rose" }[s] || "slate");
const insToneClass = (s) => ({
  Expired: "text-rose-600 dark:text-rose-400 font-semibold",
  "Expiring Soon": "text-amber-600 dark:text-amber-400 font-semibold",
}[s] || "");
const insAuditTitle = (r) => {
  const bits = [`Created by ${r.created_by_username || "—"} on ${fmtDMY(r.created_at)}`];
  if (r.updated_by_username) bits.push(`Updated by ${r.updated_by_username} on ${fmtDMY(r.updated_at)}`);
  return bits.join("\n");
};

function InsuranceModal({ assetId, existing = null, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState({
    insurance_from: existing?.insurance_from?.split("T")[0] || "",
    insurance_to: existing?.insurance_to?.split("T")[0] || "",
    insurance_company: existing?.insurance_company || "",
    policy_number: existing?.policy_number || "",
    insurance_amount: existing?.insurance_amount ?? "",
    idv: existing?.idv ?? "",
    insurance_type: existing?.insurance_type || "Comprehensive",
    premium_amount: existing?.premium_amount ?? "",
    remarks: existing?.remarks || "",
  });
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onPickFiles = (e) => {
    setFiles(Array.from(e.target.files || []));
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!f.insurance_from) missing.push("Insurance From Date");
    if (!f.insurance_to) missing.push("Insurance To Date");
    if (!f.insurance_company.trim()) missing.push("Insurance Company");
    if (!f.policy_number.trim()) missing.push("Policy Number");
    if (f.insurance_amount === "" || f.insurance_amount == null) missing.push("Insurance Amount");
    if (!isEdit && files.length === 0) missing.push("Attachment");
    if (missing.length) { setErr(`Still required: ${missing.join(", ")}`); return; }

    if (f.insurance_to < f.insurance_from) {
      setErr("Insurance To Date cannot be earlier than the From Date.");
      return;
    }
    const bad = files.find(x => !/\.(pdf|jpe?g|png)$/i.test(x.name));
    if (bad) { setErr(`"${bad.name}" is not a PDF, JPG or PNG.`); return; }
    const oversized = files.find(x => x.size > 5 * 1024 * 1024);
    if (oversized) { setErr(`"${oversized.name}" exceeds 5 MB.`); return; }

    const payload = {
      ...f,
      insurance_amount: parseFloat(f.insurance_amount) || 0,
      idv: f.idv === "" ? 0 : (parseFloat(f.idv) || 0),
      premium_amount: f.premium_amount === "" ? 0 : (parseFloat(f.premium_amount) || 0),
      insurance_company: f.insurance_company.trim(),
      policy_number: f.policy_number.trim(),
    };

    setBusy(true); setErr("");
    if (isEdit) {
      try {
        await api.updateInsurance(existing.id, payload);
        if (files.length) await api.uploadInsuranceAttachments(existing.id, files);
        onSaved();
      } catch (e2) {
        setErr(e2.response?.data?.detail || "Failed to save insurance record.");
      } finally { setBusy(false); }
      return;
    }
    let createdId = null;
    try {
      const { data: rec } = await api.addInsurance(assetId, payload);
      createdId = rec.id;
      await api.uploadInsuranceAttachments(createdId, files);
      onSaved();
    } catch (e2) {
      // Don't leave a record without its policy document.
      if (createdId) { try { await api.deleteInsurance(createdId); } catch (_) {} }
      setErr(e2.response?.data?.detail || "Failed to save insurance record. Check the file and try again.");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? "Edit insurance record" : "Add insurance"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="insurance-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Insurance From Date *">
            <Input type="date" required value={f.insurance_from}
              onChange={(e) => setF({ ...f, insurance_from: e.target.value })}
              data-testid="ins-from" />
          </Field>
          <Field label="Insurance To Date *">
            <Input type="date" required min={f.insurance_from || undefined} value={f.insurance_to}
              onChange={(e) => setF({ ...f, insurance_to: e.target.value })}
              data-testid="ins-to" />
          </Field>
        </div>
        <Field label="Insurance Company *">
          <Input required value={f.insurance_company} placeholder="e.g. Kotak Car Secure"
            onChange={(e) => setF({ ...f, insurance_company: e.target.value })}
            data-testid="ins-company" />
        </Field>
        <Field label="Policy Number *">
          <Input required value={f.policy_number} placeholder="e.g. POL-2026-0142"
            onChange={(e) => setF({ ...f, policy_number: e.target.value })}
            data-testid="ins-policy" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Insurance Amount (₹) *">
            <Input type="number" step="0.01" min="0" required value={f.insurance_amount}
              onChange={(e) => setF({ ...f, insurance_amount: e.target.value })}
              data-testid="ins-amount" />
          </Field>
          <Field label="Premium Amount (₹)">
            <Input type="number" step="0.01" min="0" value={f.premium_amount}
              onChange={(e) => setF({ ...f, premium_amount: e.target.value })}
              data-testid="ins-premium" />
          </Field>
        </div>
        <Field label="IDV — Insured Declared Value (₹)">
          <Input type="number" step="0.01" min="0" value={f.idv}
            onChange={(e) => setF({ ...f, idv: e.target.value })}
            placeholder="e.g. 760104"
            data-testid="ins-idv" />
        </Field>
        <Field label="Insurance Type">
          <Select value={f.insurance_type} onChange={(e) => setF({ ...f, insurance_type: e.target.value })}
            data-testid="ins-type">
            {INSURANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Remarks">
          <Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })}
            data-testid="ins-remarks" />
        </Field>

        <Field label={isEdit ? "Add attachment (optional)" : "Attachment (required)"}>
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={onPickFiles}
            className="block w-full text-xs text-slate-600 dark:text-slate-300
                       file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0
                       file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700
                       hover:file:bg-indigo-100 dark:file:bg-slate-700 dark:file:text-slate-200"
            data-testid="ins-attachment-input"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
            {isEdit ? "Optionally attach more policy documents. PDF, JPG or PNG — 5 MB per file."
                    : "Upload the policy document. PDF, JPG or PNG — 5 MB per file."}
          </p>
          {files.length > 0 && (
            <ul className="text-[11px] text-slate-600 dark:text-slate-300 mt-1.5 space-y-0.5" data-testid="ins-attachment-list">
              {files.map((x, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <Check size={11} className="text-emerald-500" />
                  <span className="truncate">{x.name}</span>
                  <span className="text-slate-400">({(x.size / 1024).toFixed(1)} KB)</span>
                </li>
              ))}
            </ul>
          )}
        </Field>

        {err && (
          <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 ring-1 ring-rose-100 dark:ring-rose-900/50 px-3 py-2 rounded-lg"
               data-testid="insurance-error">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="insurance-submit">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Save insurance"}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

function ConditionModal({ assetId, onClose, onSaved }) {
  const [f, setF] = useState({ snapshot_date: new Date().toISOString().slice(0,10),
    cracks_in_body: false, water_circulation_ok: true, pump_working: true, fan_working: true, motor_working: true, notes: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.addCondition(assetId, f); onSaved(); }
    catch (e2) { setErr(e2.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const Tog = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={f[k]} onChange={e=>setF({...f,[k]:e.target.checked})} data-testid={`cond-${k}`} /> {label}
    </label>
  );
  return (
    <Modal title="Add Condition Snapshot" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="condition-form">
        <Field label="Date"><Input type="date" required value={f.snapshot_date} onChange={e=>setF({...f,snapshot_date:e.target.value})} /></Field>
        <Tog k="cracks_in_body" label="Cracks in body or tank" />
        <Tog k="water_circulation_ok" label="Water circulation OK" />
        <Tog k="pump_working" label="Pump working" />
        <Tog k="fan_working" label="Fan working" />
        <Tog k="motor_working" label="Motor working" />
        <Field label="Notes"><Input value={f.notes} onChange={e=>setF({...f,notes:e.target.value})} /></Field>
        {err && <div className="text-sm text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={busy} data-testid="cond-save">Save</Btn></div>
      </form>
    </Modal>
  );
}

// ---------------- Locations admin ----------------
function LocationsPage() {
  const user = getUser();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.listLocations().then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (user?.role !== "admin") return <Forbidden />;
  const del = async (id) => {
    if (!window.confirm("Delete location?")) return;
    try { await api.deleteLocation(id); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-8 space-y-4" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Locations</h1>
        <Btn onClick={()=>setShowCreate(true)} data-testid="new-location-btn"><Plus size={14} className="inline mr-1" />New Location</Btn>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Name</th><th>Code</th><th>Description</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map(l => (
              <tr key={l.id} className="table-row" data-testid={`location-row-${l.code}`}>
                <td className="px-4 py-2 font-medium">{l.name}</td>
                <td className="font-mono text-xs">{l.code}</td>
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{l.description}</td>
                <td><Badge color={l.is_active ? "green" : "slate"}>{l.is_active ? "Active" : "Inactive"}</Badge></td>
                <td className="text-right pr-3">
                  <Btn variant="ghost" onClick={()=>setEditing(l)}>Edit</Btn>
                  <Btn variant="ghost" onClick={()=>del(l.id)}>Delete</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {(showCreate || editing) && <LocationModal initial={editing} onClose={()=>{setShowCreate(false); setEditing(null);}} onSaved={()=>{setShowCreate(false); setEditing(null); load();}} />}
    </div>
  );
}
function LocationModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { name: "", code: "", description: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (initial) await api.updateLocation(initial.id, { name: f.name, code: f.code, description: f.description, is_active: f.is_active });
      else await api.createLocation({ name: f.name, code: f.code, description: f.description });
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial ? "Edit Location" : "New Location"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="location-form">
        <Field label="Name"><Input required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="loc-name" /></Field>
        <Field label="Code"><Input required value={f.code} onChange={e=>setF({...f,code:e.target.value})} data-testid="loc-code" /></Field>
        <Field label="Description"><Input value={f.description || ""} onChange={e=>setF({...f,description:e.target.value})} /></Field>
        {err && <div className="text-sm text-rose-600" data-testid="loc-error">{err}</div>}
        <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={busy} data-testid="loc-save">Save</Btn></div>
      </form>
    </Modal>
  );
}

// ---------------- Areas admin ----------------
function AreasPage() {
  const user = getUser();
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState("");
  const [areas, setAreas] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.listLocations().then(r => {
      setLocations(r.data);
      if (r.data[0]?.id) setSelectedLoc(r.data[0].id);
    });
  }, []);
  const load = () => {
    if (!selectedLoc) { setAreas([]); return; }
    api.listAreas({ location_id: selectedLoc }).then(r => setAreas(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [selectedLoc]);
  if (user?.role !== "admin") return <Forbidden />;
  const del = async (id) => {
    if (!window.confirm("Delete area?")) return;
    try { await api.deleteArea(id); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-8 space-y-4" data-testid="areas-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Areas</h1>
        <Btn onClick={()=>setShowCreate(true)} disabled={!selectedLoc} data-testid="new-area-btn"><Plus size={14} className="inline mr-1" />New Area</Btn>
      </div>
      <Card className="p-4">
        <Field label="Location">
          <Select value={selectedLoc} onChange={e=>setSelectedLoc(e.target.value)} data-testid="area-location-filter">
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </Field>
      </Card>
      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Name</th><th>Code</th><th>Description</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {areas.map(a => (
              <tr key={a.id} className="table-row" data-testid={`area-row-${a.code || a.id}`}>
                <td className="px-4 py-2 font-medium">{a.name}</td>
                <td className="font-mono text-xs">{a.code || "—"}</td>
                <td className="text-slate-600 dark:text-slate-300 dark:text-slate-300">{a.description}</td>
                <td><Badge color={a.is_active ? "green" : "slate"}>{a.is_active ? "Active" : "Inactive"}</Badge></td>
                <td className="text-right pr-3">
                  <Btn variant="ghost" onClick={()=>setEditing(a)}>Edit</Btn>
                  <Btn variant="ghost" onClick={()=>del(a.id)}>Delete</Btn>
                </td>
              </tr>
            ))}
            {!areas.length && <tr><td colSpan={5} className="text-center py-6 text-slate-500" data-testid="no-areas-msg">No areas at this location yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      {(showCreate || editing) && (
        <AreaModal
          initial={editing}
          locationId={selectedLoc}
          onClose={()=>{setShowCreate(false); setEditing(null);}}
          onSaved={()=>{setShowCreate(false); setEditing(null); load();}}
        />
      )}
    </div>
  );
}
function AreaModal({ initial, locationId, onClose, onSaved }) {
  const [f, setF] = useState(initial || { name: "", code: "", description: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (initial) {
        await api.updateArea(initial.id, { name: f.name, code: f.code, description: f.description, is_active: f.is_active });
      } else {
        await api.createArea({ location_id: locationId, name: f.name, code: f.code, description: f.description });
      }
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial ? "Edit Area" : "New Area"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="area-form">
        <Field label="Name"><Input required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="area-name" /></Field>
        <Field label="Code (optional, unique per location)"><Input value={f.code || ""} onChange={e=>setF({...f,code:e.target.value})} data-testid="area-code" /></Field>
        <Field label="Description"><Input value={f.description || ""} onChange={e=>setF({...f,description:e.target.value})} /></Field>
        {err && <div className="text-sm text-rose-600" data-testid="area-error">{err}</div>}
        <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={busy} data-testid="area-save">Save</Btn></div>
      </form>
    </Modal>
  );
}

// ---------------- Categories admin ----------------
function CategoriesPage() {
  const user = getUser();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.listCategories().then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (user?.role !== "admin") return <Forbidden />;
  const del = async (code) => {
    if (!window.confirm(`Delete category "${code}"? This is blocked if any assets still use it.`)) return;
    try { await api.deleteCategory(code); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-8 space-y-4" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Categories</h1>
        <Btn onClick={()=>setShowCreate(true)} data-testid="new-category-btn"><Plus size={14} className="inline mr-1" />New Category</Btn>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Name</th><th>Code</th><th>ID Prefix</th><th>Fields</th><th>Maint. log</th><th></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.code} className="table-row" data-testid={`category-row-${c.code}`}>
                <td className="px-4 py-2 font-medium">{c.name}</td>
                <td className="font-mono text-xs">{c.code}</td>
                <td className="font-mono text-xs">{c.id_prefix}</td>
                <td>{(c.extra_fields || []).length}</td>
                <td><Badge color={c.has_maintenance_log ? "green" : "slate"}>{c.has_maintenance_log ? "Yes" : "No"}</Badge></td>
                <td className="text-right pr-3">
                  <Btn variant="ghost" onClick={()=>setEditing(c)}>Edit</Btn>
                  <Btn variant="ghost" onClick={()=>del(c.code)}>Delete</Btn>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="text-center py-6 text-slate-500" data-testid="no-categories-msg">No categories yet.</td></tr>}
          </tbody>
        </table>
      </Card>
      {(showCreate || editing) && (
        <CategoryModal
          initial={editing}
          onClose={()=>{setShowCreate(false); setEditing(null);}}
          onSaved={()=>{setShowCreate(false); setEditing(null); load();}}
        />
      )}
    </div>
  );
}

function CategoryModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState(initial ? {
    code: initial.code, name: initial.name, id_prefix: initial.id_prefix,
    has_maintenance_log: !!initial.has_maintenance_log,
    extra_fields: (initial.extra_fields || []).map(x => ({ ...x })),
  } : { code: "", name: "", id_prefix: "", has_maintenance_log: true, extra_fields: [] });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const setFieldVal = (i, key, val) => setF(s => {
    const ef = s.extra_fields.slice(); ef[i] = { ...ef[i], [key]: val }; return { ...s, extra_fields: ef };
  });
  const addField = () => setF(s => ({ ...s, extra_fields: [...s.extra_fields, { label: "", name: "", type: "string", required: false }] }));
  const removeField = (i) => setF(s => ({ ...s, extra_fields: s.extra_fields.filter((_, j) => j !== i) }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const fields = f.extra_fields.map(x => ({
        name: (x.name || x.label || "").trim(),
        label: (x.label || x.name || "").trim(),
        type: ["string", "number", "date"].includes(x.type) ? x.type : "string",
        required: !!x.required,
      })).filter(x => x.label || x.name);
      if (isEdit) {
        await api.updateCategory(initial.code, { name: f.name, has_maintenance_log: f.has_maintenance_log, extra_fields: fields });
      } else {
        await api.createCategory({ name: f.name, has_maintenance_log: f.has_maintenance_log, extra_fields: fields });
      }
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? `Edit Category — ${initial.code}` : "New Category"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="category-form">
        {isEdit && (
          <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="category-codeinfo">
            Code <span className="font-mono text-slate-700 dark:text-slate-200">{f.code}</span>
            <span className="mx-2">·</span>
            Asset ID prefix <span className="font-mono text-slate-700 dark:text-slate-200">{f.id_prefix}</span>
          </div>
        )}
        <Field label="Name"><Input required value={f.name} onChange={e=>setF({ ...f, name: e.target.value })} data-testid="category-name" /></Field>
        {!isEdit && <p className="text-[11px] text-slate-400 -mt-1" data-testid="category-autohint">A category code and an asset-ID prefix (e.g. MON-001) are generated automatically from the name.</p>}
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input type="checkbox" checked={f.has_maintenance_log} onChange={e=>setF({ ...f, has_maintenance_log: e.target.checked })} data-testid="category-maint" />
          Has maintenance log
        </label>
        <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Custom fields</span>
            <Btn type="button" variant="secondary" onClick={addField} data-testid="category-add-field"><Plus size={14} className="inline mr-1" />Add field</Btn>
          </div>
          {!f.extra_fields.length && <div className="text-xs text-slate-500 mb-2">No custom fields yet.</div>}
          <div className="space-y-2">
            {f.extra_fields.map((fl, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`category-field-row-${i}`}>
                <div className="col-span-4"><Input placeholder="Label" value={fl.label} onChange={e=>setFieldVal(i, "label", e.target.value)} /></div>
                <div className="col-span-3"><Input placeholder="key (optional)" value={fl.name} onChange={e=>setFieldVal(i, "name", e.target.value)} /></div>
                <div className="col-span-2"><Select value={fl.type} onChange={e=>setFieldVal(i, "type", e.target.value)}>
                  <option value="string">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                </Select></div>
                <label className="col-span-2 flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={!!fl.required} onChange={e=>setFieldVal(i, "required", e.target.checked)} />Req
                </label>
                <div className="col-span-1 text-right"><Btn type="button" variant="ghost" onClick={()=>removeField(i)} aria-label="Remove field"><Trash2 size={14} /></Btn></div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Removing a field hides it from forms but keeps existing saved data — re-add the same key to bring it back.</p>
        </div>
        {err && <div className="text-sm text-rose-600" data-testid="category-error">{err}</div>}
        <div className="flex justify-end gap-2"><Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={busy} data-testid="category-save">Save</Btn></div>
      </form>
    </Modal>
  );
}

// ---------------- Users admin ----------------
function UsersPage() {
  const user = getUser();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => { api.listUsers().then(r => setItems(r.data)); api.listLocations().then(r => setLocations(r.data)); };
  useEffect(() => { load(); }, []);
  if (user?.role !== "admin") return <Forbidden />;
  const del = async (id) => { if (!window.confirm("Delete user?")) return; await api.deleteUser(id); load(); };
  const locName = (id) => locations.find(l => l.id === id)?.name || "—";
  return (
    <div className="p-8 space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Users</h1>
        <Btn onClick={()=>setShowCreate(true)} data-testid="new-user-btn"><Plus size={14} className="inline mr-1" />New User</Btn>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Username</th><th>Name</th><th>Role</th><th>Location</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map(u => (
              <tr key={u.id} className="table-row" data-testid={`user-row-${u.username}`}>
                <td className="px-4 py-2 font-mono">{u.username}</td>
                <td>{u.name}</td>
                <td><Badge color="indigo">{u.role}</Badge></td>
                <td>{u.location_id ? locName(u.location_id) : "—"}</td>
                <td><Badge color={u.is_active ? "green" : "slate"}>{u.is_active ? "Yes" : "No"}</Badge></td>
                <td className="text-right pr-3">
                  <Btn variant="ghost" onClick={()=>setEditing(u)}>Edit</Btn>
                  <Btn variant="ghost" onClick={()=>del(u.id)}>Delete</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {(showCreate || editing) && <UserModal initial={editing} locations={locations} onClose={()=>{setShowCreate(false); setEditing(null);}} onSaved={()=>{setShowCreate(false); setEditing(null); load();}} />}
    </div>
  );
}
function UserModal({ initial, locations, onClose, onSaved }) {
  const [f, setF] = useState(initial ? { ...initial, password: "" } : { username: "", name: "", email: "", password: "", role: "viewer", location_id: null });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const body = { ...f };
      if (!body.password) delete body.password;
      if (body.role !== "location_editor") body.location_id = null;
      if (initial) {
        delete body.id; delete body.username; delete body.created_at; delete body.password_hash;
        await api.updateUser(initial.id, body);
      } else {
        await api.createUser(body);
      }
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial ? "Edit User" : "New User"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="user-form">
        <Field label="Username"><Input required value={f.username} onChange={e=>setF({...f,username:e.target.value})} disabled={!!initial} data-testid="user-username" /></Field>
        <Field label="Name"><Input required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="user-name" /></Field>
        <Field label="Email"><Input type="email" value={f.email || ""} onChange={e=>setF({...f,email:e.target.value})} /></Field>
        <Field label={initial ? "New Password (leave blank to keep)" : "Password"}>
          <Input type="password" required={!initial} value={f.password || ""} onChange={e=>setF({...f,password:e.target.value})} data-testid="user-password" />
        </Field>
        <Field label="Role">
          <Select value={f.role} onChange={e=>setF({...f,role:e.target.value, location_id: e.target.value === "location_editor" ? f.location_id : null})} data-testid="user-role">
            <option value="admin">Admin (global)</option>
            <option value="location_editor">Location Editor (scoped)</option>
            <option value="viewer">Viewer (read-only)</option>
          </Select>
        </Field>
        {f.role === "location_editor" && (
          <Field label="Location">
            <Select required value={f.location_id || ""} onChange={e=>setF({...f,location_id:e.target.value})} data-testid="user-location">
              <option value="">— select —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
        )}
        {initial && <Field label="Active"><Select value={String(f.is_active !== false)} onChange={e=>setF({...f,is_active:e.target.value==="true"})}><option value="true">Yes</option><option value="false">No</option></Select></Field>}
        {err && <div className="text-sm text-rose-600" data-testid="user-error">{err}</div>}
        <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={onClose}>Cancel</Btn><Btn type="submit" disabled={busy} data-testid="user-save">Save</Btn></div>
      </form>
    </Modal>
  );
}

// ---------------- Reports ----------------
const REPORT_DEFAULTS = { location_id: "", category_code: "", date_from: "", date_to: "" };
const fmtMoney = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const fmtMonth = (m) => {
  const [y, mm] = String(m).split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(mm, 10) - 1] || mm} ${y}`;
};

/** Horizontal bar list — same visual language as the dashboard's location chart. */
function BarList({ rows, format = (v) => v, empty = "Nothing to show.", limit = 10, testid }) {
  const shown = rows.slice(0, limit);
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  if (!rows.length) return <div className="text-sm text-slate-500 dark:text-slate-400 py-4">{empty}</div>;
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {shown.map((r) => (
        <div key={r.key ?? r.name}>
          <div className="flex items-center justify-between text-xs mb-1 gap-3">
            <span className="text-slate-700 dark:text-slate-300 truncate">{r.name}</span>
            <span className="text-slate-500 dark:text-slate-400 tabular-nums flex-shrink-0">{format(r.value)}{r.sub ? <span className="text-slate-400 dark:text-slate-500"> · {r.sub}</span> : null}</span>
          </div>
          <div className="bar-track h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
        </div>
      ))}
      {rows.length > shown.length && (
        <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-1">+{rows.length - shown.length} more</div>
      )}
    </div>
  );
}

function ReportsPage() {
  const [filters, setFilters] = useState(REPORT_DEFAULTS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.listLocations().then(r => setLocations(r.data)).catch(()=>{});
    api.listCategories().then(r => setCategories(r.data)).catch(()=>{});
  }, []);
  useEffect(() => {
    setLoading(true); setFailed(false);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.reportSummary(params)
      .then(r => setData(r.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const locName = (id) => locations.find(l => l.id === id)?.name || (id ? "Unknown" : "—");
  const catName = (code) => categories.find(c => c.code === code)?.name || code || "—";

  // Drill into the assets list carrying the report's own scope. status="" so the
  // list shows every status, matching how the report counts them.
  const assetsLink = (extra) => {
    const p = new URLSearchParams();
    if (filters.location_id) p.set("location_id", filters.location_id);
    if (filters.category_code) p.set("category_code", filters.category_code);
    p.set("status", "");
    Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)));
    return `/assets?${p.toString()}`;
  };

  const DOWNLOADS = {
    // This report, one sheet per section on the page.
    report: { name: "report", call: (p) => api.exportReport(p), dated: true },
    // The underlying rows behind it.
    assets: { name: "asset-register", call: (p) => api.exportAssetRegister(p) },
    logs: { name: "service-logs", call: (p) => api.exportMaintenance(p), dated: true },
  };

  const download = async (kind) => {
    const spec = DOWNLOADS[kind];
    setBusy(kind);
    try {
      const params = {
        location_id: filters.location_id || undefined,
        category_code: filters.category_code || undefined,
        ...(spec.dated ? { date_from: filters.date_from || undefined, date_to: filters.date_to || undefined } : {}),
      };
      const { data: blob } = await spec.call(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${spec.name}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Download failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const dirty = JSON.stringify(filters) !== JSON.stringify(REPORT_DEFAULTS);
  const m = data?.maintenance;
  const a = data?.assets;
  const statusOrder = ["Active", "Inactive", "Retired"];

  return (
    <div className="p-8 space-y-5" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Asset register, expiry &amp; compliance, and maintenance spend — all in one place.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0 no-print">
          <Btn onClick={()=>download("report")} disabled={!!busy} data-testid="export-report">
            <Download size={14} className="inline mr-1" />{busy==="report" ? "Preparing…" : "Download report"}
          </Btn>
          <Btn variant="secondary" onClick={()=>download("assets")} disabled={!!busy} data-testid="export-asset-register">
            <Download size={14} className="inline mr-1" />{busy==="assets" ? "Preparing…" : "Asset register"}
          </Btn>
          <Btn variant="secondary" onClick={()=>download("logs")} disabled={!!busy} data-testid="export-service-logs">
            <Download size={14} className="inline mr-1" />{busy==="logs" ? "Preparing…" : "Service logs"}
          </Btn>
          <Btn variant="secondary" onClick={()=>window.print()} data-testid="print-report" title="Print or save as PDF">
            <FileText size={14} className="inline mr-1" />PDF
          </Btn>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-5 gap-3">
          <Field label="Location">
            <Select value={filters.location_id} onChange={e=>setFilters(f=>({...f, location_id: e.target.value}))} data-testid="report-filter-location">
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label="Category">
            <Select value={filters.category_code} onChange={e=>setFilters(f=>({...f, category_code: e.target.value}))} data-testid="report-filter-category">
              <option value="">All categories</option>
              {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Service from">
            <Input type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f, date_from: e.target.value}))} data-testid="report-filter-from" />
          </Field>
          <Field label="Service to">
            <Input type="date" value={filters.date_to} onChange={e=>setFilters(f=>({...f, date_to: e.target.value}))} data-testid="report-filter-to" />
          </Field>
          <div className="flex items-end no-print">
            <Btn variant="secondary" onClick={()=>setFilters(REPORT_DEFAULTS)} disabled={!dirty} data-testid="report-reset">Reset</Btn>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2">
          Location and category scope the whole report. The date range applies to service logs and spend only.
        </p>
      </Card>

      {loading && <Card className="p-8 text-sm text-slate-500" data-testid="reports-loading">Building report…</Card>}
      {failed && !loading && <Card className="p-8 text-sm text-rose-600" data-testid="reports-error">Could not load the report. Try again.</Card>}

      {!loading && !failed && data && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Link to={assetsLink({})} className="block" data-testid="report-kpi-assets-link">
              <KPICard label="Assets in scope" value={a.total} icon={Package} accent="text-indigo-500" hint="View list"
                       sub={statusOrder.filter(s => a.by_status[s]).map(s => `${a.by_status[s]} ${s.toLowerCase()}`).join(" · ") || "—"}
                       testid="report-kpi-assets" />
            </Link>
            <KPICard label="Service logs" value={m.log_count} icon={Wrench} accent="text-emerald-500"
                     sub={filters.date_from || filters.date_to ? "in selected range" : "all time"} testid="report-kpi-logs" />
            <KPICard label="Maintenance spend" value={fmtMoney(m.total_spend)} icon={Activity} accent="text-amber-500"
                     sub={m.log_count ? `${fmtMoney(m.avg_per_log)} avg per log` : "—"} testid="report-kpi-spend" />
            <KPICard label="Assets serviced" value={m.assets_serviced} icon={ClipboardList} accent="text-sky-500"
                     sub={m.assets_never_serviced != null ? `${m.assets_never_serviced} never serviced` : "in selected range"}
                     testid="report-kpi-serviced" />
          </div>

          <Card className="p-5" data-testid="report-compliance">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-500" /> Expiry &amp; compliance
              </h2>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">Windows are cumulative · click any number to see the assets</span>
            </div>
            <table className="w-full text-sm">
              <thead className="table-head text-left">
                <tr>
                  <th className="px-3 py-2">Item</th>
                  <th className="text-right px-3">Expired</th>
                  <th className="text-right px-3">Due ≤30d</th>
                  <th className="text-right px-3">Due ≤60d</th>
                  <th className="text-right px-3">Due ≤90d</th>
                  <th className="text-right px-3">Not set</th>
                  <th className="text-right px-3">In scope</th>
                </tr>
              </thead>
              <tbody>
                {data.compliance.map(row => (
                  <tr key={row.key} className="table-row" data-testid={`report-compliance-${row.key}`}>
                    <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-200">{row.label}</td>
                    {row.n_a ? (
                      <td colSpan={6} className="text-right px-3 text-slate-400 dark:text-slate-500 text-xs">
                        Not applicable to the selected category
                      </td>
                    ) : (
                      <>
                        <CountCell n={row.expired} to={assetsLink({ expired: row.key })} tone="rose" />
                        <CountCell n={row.d30} to={assetsLink({ [row.param]: 30 })} tone="amber" />
                        <CountCell n={row.d60} to={assetsLink({ [row.param]: 60 })} />
                        <CountCell n={row.d90} to={assetsLink({ [row.param]: 90 })} />
                        <td className="text-right px-3 tabular-nums text-slate-400 dark:text-slate-500">{row.not_set}</td>
                        <td className="text-right px-3 tabular-nums text-slate-500 dark:text-slate-400">{row.scope_total}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Assets by category</h2>
              <BarList testid="report-by-category" limit={12}
                rows={Object.entries(a.by_category).map(([code, n]) => ({ key: code, name: catName(code), value: n })).sort((x,y)=>y.value-x.value)} />
            </Card>
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Assets by location</h2>
              <BarList testid="report-by-location"
                rows={Object.entries(a.by_location).map(([id, n]) => ({ key: id, name: locName(id), value: n })).sort((x,y)=>y.value-x.value)} />
            </Card>
          </div>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Assets by department</h2>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">{Object.keys(a.by_department).filter(Boolean).length} departments</span>
            </div>
            <BarList testid="report-by-department" limit={12}
              empty="No departments recorded on these assets."
              rows={Object.entries(a.by_department).filter(([d]) => d).map(([d, n]) => ({ key: d, name: d, value: n })).sort((x,y)=>y.value-x.value)} />
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Wrench size={14} className="text-emerald-500" /> Maintenance spend by month
              </h2>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">{m.by_month.length} months with activity</span>
            </div>
            <BarList testid="report-spend-by-month" limit={18} format={fmtMoney}
              empty="No service logs in this range."
              rows={m.by_month.map(x => ({ key: x.month, name: fmtMonth(x.month), value: x.amount, sub: `${x.count} log${x.count === 1 ? "" : "s"}` }))} />
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Spend by category</h2>
              <BarList testid="report-spend-by-category" format={fmtMoney}
                empty="No spend recorded."
                rows={m.by_category.map(x => ({ key: x.category_code, name: catName(x.category_code), value: x.amount, sub: `${x.count} log${x.count === 1 ? "" : "s"}` }))} />
            </Card>
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Spend by location</h2>
              <BarList testid="report-spend-by-location" format={fmtMoney}
                empty="No spend recorded."
                rows={m.by_location.map(x => ({ key: x.location_id, name: locName(x.location_id), value: x.amount, sub: `${x.count} log${x.count === 1 ? "" : "s"}` }))} />
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Top assets by spend</h2>
              {!m.top_assets.length ? (
                <div className="text-sm text-slate-500 dark:text-slate-400 py-4">No spend recorded.</div>
              ) : (
                <table className="w-full text-sm" data-testid="report-top-assets">
                  <thead className="table-head text-left">
                    <tr><th className="px-3 py-2">Asset</th><th className="text-right px-3">Logs</th><th className="text-right px-3">Spend</th></tr>
                  </thead>
                  <tbody>
                    {m.top_assets.map(x => (
                      <tr key={x.id} className="table-row">
                        <td className="px-3 py-2">
                          <Link to={`/assets/${x.id}`} className="text-indigo-600 hover:underline">
                            <span className="font-mono text-xs text-slate-500 dark:text-slate-400 mr-2">{x.asset_id}</span>{x.name}
                          </Link>
                        </td>
                        <td className="text-right px-3 tabular-nums text-slate-500 dark:text-slate-400">{x.count}</td>
                        <td className="text-right px-3 tabular-nums font-medium">{fmtMoney(x.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Top vendors by spend</h2>
              {!m.top_vendors.length ? (
                <div className="text-sm text-slate-500 dark:text-slate-400 py-4">No vendors recorded.</div>
              ) : (
                <table className="w-full text-sm" data-testid="report-top-vendors">
                  <thead className="table-head text-left">
                    <tr><th className="px-3 py-2">Vendor</th><th className="text-right px-3">Jobs</th><th className="text-right px-3">Spend</th></tr>
                  </thead>
                  <tbody>
                    {m.top_vendors.map(x => (
                      <tr key={x.key} className="table-row">
                        <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{x.vendor}</td>
                        <td className="text-right px-3 tabular-nums text-slate-500 dark:text-slate-400">{x.count}</td>
                        <td className="text-right px-3 tabular-nums font-medium">{fmtMoney(x.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card className="p-5" data-testid="report-history-card">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Wrench size={14} className="text-emerald-500" /> Maintenance history
                <span className="text-slate-400 dark:text-slate-500 font-normal">({m.history_total})</span>
                {m.total_spend > 0 && <Badge color="emerald">{fmtMoney(m.total_spend)} total</Badge>}
              </h2>
              <Btn variant="secondary" onClick={()=>download("logs")} disabled={!!busy || !m.history_total}
                   title="Download every service log in this scope" data-testid="export-history">
                <Download size={14} className="inline mr-1" />{busy==="logs" ? "Preparing…" : "Export"}
              </Btn>
            </div>
            {!m.history.length ? (
              <div className="text-sm text-slate-500 dark:text-slate-400 py-4" data-testid="report-history-empty">
                No service logs for this scope.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="report-history">
                    <thead className="table-head text-left">
                      <tr>
                        <th className="px-3 py-2 whitespace-nowrap">Date</th>
                        <th className="pr-4 whitespace-nowrap">Asset</th>
                        <th className="pr-4">Category</th>
                        <th className="pr-4">Location</th>
                        <th className="pr-4">Service by</th>
                        <th className="pr-4">Details</th>
                        <th className="text-right px-3 whitespace-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.history.map(h => (
                        <tr key={h.id} className="table-row">
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDMY(h.date)}</td>
                          <td className="pr-4 whitespace-nowrap">
                            {h.asset_key ? (
                              <Link to={`/assets/${h.asset_key}`} className="text-indigo-600 hover:underline">
                                <span className="font-mono text-xs text-slate-500 dark:text-slate-400 mr-2">{h.asset_id}</span>{h.asset_name}
                              </Link>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="pr-4 text-slate-600 dark:text-slate-300">{catName(h.category_code)}</td>
                          <td className="pr-4 text-slate-600 dark:text-slate-300">{locName(h.location_id)}</td>
                          <td className="pr-4 text-slate-600 dark:text-slate-300">{h.vendor || "—"}</td>
                          <td className="pr-4 text-slate-500 dark:text-slate-400 max-w-[14rem] truncate" title={h.details}>{h.details || "—"}</td>
                          <td className="text-right px-3 tabular-nums font-medium">{h.amount > 0 ? fmtMoney(h.amount) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {m.history_total > m.history.length && (
                  <div className="text-[11px] text-slate-400 dark:text-slate-500 pt-3">
                    Showing the {m.history.length} most recent of {m.history_total} — Export downloads all of them.
                  </div>
                )}
              </>
            )}
          </Card>

          <p className="text-[11px] text-slate-400 dark:text-slate-500 pb-2" data-testid="report-generated-at">
            Generated {new Date(data.generated_at).toLocaleString("en-IN")}
          </p>
        </>
      )}
    </div>
  );
}

/** A compliance count that links to the matching assets view; 0 stays inert. */
function CountCell({ n, to, tone }) {
  const toneCls = n > 0
    ? ({ rose: "text-rose-600 dark:text-rose-400 font-semibold", amber: "text-amber-600 dark:text-amber-400 font-semibold" }[tone] || "text-slate-700 dark:text-slate-200")
    : "text-slate-300 dark:text-slate-600";
  return (
    <td className="text-right px-3 tabular-nums">
      {n > 0 ? <Link to={to} className={`${toneCls} hover:underline`}>{n}</Link> : <span className={toneCls}>0</span>}
    </td>
  );
}

// ---------------- Audit ----------------
function AuditPage() {
  const user = getUser();
  const [items, setItems] = useState([]);
  useEffect(() => { api.audit(300).then(r => setItems(r.data)).catch(()=>{}); }, []);
  if (user?.role !== "admin") return <Forbidden />;
  return (
    <div className="p-8 space-y-4" data-testid="audit-page">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Audit log</h1>
      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Time</th><th>User</th><th>Action</th><th>Resource</th><th>Details</th></tr></thead>
          <tbody>
            {items.map(a => (
              <tr key={a.id} className="table-row">
                <td className="px-4 py-2 font-mono text-xs">{a.timestamp?.split("T").join(" ").slice(0,19)}</td>
                <td>{a.username} <span className="text-xs text-slate-400 dark:text-slate-500 dark:text-slate-500">({a.role})</span></td>
                <td><Badge color="indigo">{a.action}</Badge></td>
                <td>{a.resource_type}{a.resource_id ? <span className="text-xs text-slate-400 dark:text-slate-500 dark:text-slate-500 ml-1">{a.resource_id.slice(-6)}</span> : ""}</td>
                <td className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 max-w-md truncate">{JSON.stringify(a.details)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------- Backup & Restore ----------------
function BackupPage() {
  const user = getUser();
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => api.backupList().then(r => setBackups(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  if (user?.role !== "admin") return <Forbidden />;

  const doExport = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.backupExport();
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `uat-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      setMsg("Backup downloaded.");
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const doRestore = async (file) => {
    if (!file) return;
    if (!window.confirm("Restore this backup? This will REPLACE all current data (a pre-restore snapshot is auto-saved).")) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.backupRestore(file, true);
      setMsg("Restore complete: " + JSON.stringify(data.restored));
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.importExcel(file);
      setMsg("Import done: " + JSON.stringify(data));
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="backup-page">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 flex items-center gap-2"><Database size={20} /> Backup &amp; restore</h1>
      <Card className="p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Export full backup</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">Downloads a JSON file with all users, locations, assets, maintenance logs, conditions and audit log.</p>
        <Btn onClick={doExport} disabled={busy} data-testid="backup-export-btn"><Download size={14} className="inline mr-1" />Download backup</Btn>
      </Card>
      <Card className="p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Restore from backup</h2>
        <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">⚠ This REPLACES all current data. A pre-restore snapshot is automatically saved on the server before the restore runs.</p>
        <input type="file" accept="application/json" onChange={e => doRestore(e.target.files?.[0])} className="text-sm" data-testid="backup-restore-input" />
      </Card>
      <Card className="p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Import from Excel (UTLITIES MANAGEMENT.xlsx)</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">Appends new assets. Existing asset_ids are skipped (safe to re-run).</p>
        <input type="file" accept=".xlsx,.xls" onChange={e => doImport(e.target.files?.[0])} className="text-sm" data-testid="excel-import-input" />
      </Card>
      {msg && <Card className="p-3 text-sm bg-emerald-50 text-emerald-800" data-testid="backup-msg">{msg}</Card>}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100 mb-3">Backup History</h2>
        <table className="w-full text-sm">
          <thead className="table-head text-left"><tr><th className="px-4 py-2">Time</th><th>Filename</th><th>Size</th><th>Kind</th></tr></thead>
          <tbody data-testid="backup-history-body">
            {backups.map(b => (
              <tr key={b.id} className="table-row">
                <td className="px-4 py-2">{b.created_at?.split("T").join(" ").slice(0,19)}</td>
                <td className="font-mono text-xs">{b.filename}</td>
                <td>{(b.size_bytes/1024).toFixed(1)} KB</td>
                <td>{b.kind || "export"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------------- Misc ----------------
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="modal-overlay">
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:text-slate-300 dark:text-slate-500 dark:hover:text-slate-200 transition"
            aria-label="Close"
            data-testid="modal-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 text-slate-900 dark:text-slate-100">{children}</div>
      </div>
    </div>
  );
}
const Forbidden = () => (
  <div className="p-12 text-center" data-testid="forbidden-page">
    <ShieldAlert size={48} className="mx-auto text-rose-500 mb-3" />
    <h2 className="text-xl font-bold">Forbidden</h2>
    <p className="text-slate-500">This area is restricted to admins.</p>
  </div>
);

// ---------------- Maintenance (cross-asset list) ----------------
function MaintenancePage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ category_code: "", location_id: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.listCategories().then(r => setCategories(r.data));
    api.listLocations().then(r => setLocations(r.data));
  }, []);

  const load = () => {
    const p = { ...filters, page, page_size: 50 };
    Object.keys(p).forEach(k => p[k] === "" && delete p[k]);
    api.listAllMaintenance(p).then(r => { setItems(r.data.items); setTotal(r.data.total); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [JSON.stringify(filters), page]);

  const onExportFiltered = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => params[k] === "" && delete params[k]);
      const { data } = await api.exportMaintenance(params);
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maintenance-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed. Please try again.");
    }
  };

  const catName = (code) => categories.find(c => c.code === code)?.name || code;
  const locName = (id) => locations.find(l => l.id === id)?.name || "—";

  return (
    <div className="p-8 space-y-5" data-testid="maintenance-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100" data-testid="maintenance-page-title">
            Service logs <span className="text-base font-normal text-slate-400 dark:text-slate-500 dark:text-slate-500">({total})</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500 mt-1">Recent maintenance and service records across all assets</p>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-5 gap-3">
          <Field label="Category">
            <Select value={filters.category_code} onChange={e=>setFilters(f=>({...f,category_code:e.target.value}))} data-testid="maintenance-filter-category">
              <option value="">All categories</option>
              {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Location">
            <Select value={filters.location_id} onChange={e=>setFilters(f=>({...f,location_id:e.target.value}))} data-testid="maintenance-filter-location">
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
          <Field label="From">
            <Input type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))} data-testid="maintenance-filter-from" />
          </Field>
          <Field label="To">
            <Input type="date" value={filters.date_to} onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))} data-testid="maintenance-filter-to" />
          </Field>
          <div className="flex items-end gap-2">
            <Btn variant="secondary" onClick={()=>{setFilters({category_code:"",location_id:"",date_from:"",date_to:""}); setPage(1);}} data-testid="maintenance-reset-btn">Reset</Btn>
            <Btn variant="secondary" onClick={onExportFiltered} data-testid="export-maintenance-filtered-btn">
              <Download size={14} className="inline mr-1.5" />
              Export
            </Btn>
          </div>
        </div>
      </Card>

      <Card>
        <table className="w-full text-sm">
          <thead className="table-head text-left">
            <tr className="divide-x divide-slate-200 dark:divide-slate-800">
              <th className="px-4 py-2.5">Date</th>
              <th>Asset</th>
              <th>Category</th>
              <th>Location</th>
              <th>Service by</th>
              <th className="text-right">Amount</th>
              <th>Logged by</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="maintenance-table-body">
            {items.map(l => {
              const a = l.asset;
              return (
                <tr key={l.id} className="table-row divide-x divide-slate-100 dark:divide-slate-800" data-testid={`maintenance-row-${l.id}`}>
                  <td className="px-4 py-2.5">{fmtDMY(l.date_of_service)}</td>
                  <td>
                    {a ? (
                      <Link to={`/assets/${a.id}`} className="text-indigo-600 hover:underline" data-testid={`maintenance-open-asset-${a.asset_id}`}>
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">{a.asset_id}</span> · {a.name}
                      </Link>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500 dark:text-slate-500">(deleted)</span>
                    )}
                  </td>
                  <td>{a ? catName(a.category_code) : "—"}</td>
                  <td>{a ? locName(a.location_id) : "—"}</td>
                  <td>{l.service_done_by || "—"}</td>
                  <td className="text-right tabular-nums">{l.amount > 0 ? `₹${l.amount.toLocaleString("en-IN")}` : "—"}</td>
                  <td className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500 dark:text-slate-500">{l.created_by_username || "—"}</td>
                  <td className="text-right pr-3">
                    {a && <Link to={`/assets/${a.id}`} className="text-indigo-600 hover:underline text-xs">Open</Link>}
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500" data-testid="maintenance-empty-msg">
                No maintenance logs match these filters.
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="flex items-center justify-between p-3 border-t border-slate-100 text-sm">
          <div>Showing {items.length} of {total}</div>
          <div className="flex gap-2">
            <Btn variant="secondary" disabled={page<=1} onClick={()=>setPage(p=>p-1)} data-testid="maintenance-page-prev">Prev</Btn>
            <span className="px-2 py-1">Page {page}</span>
            <Btn variant="secondary" disabled={items.length<50} onClick={()=>setPage(p=>p+1)} data-testid="maintenance-page-next">Next</Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------------- QR Sheet ----------------
function QrSheetPage() {
  const user = getUser();
  const isAdmin = user?.role === "admin";
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ category_code: "", location_id: "" });
  useEffect(() => {
    if (!isAdmin) return;
    api.listCategories().then(r => setCategories(r.data)).catch(()=>{});
    api.listLocations().then(r => setLocations(r.data)).catch(()=>{});
  }, [isAdmin]);
  if (!isAdmin) return <Forbidden />;
  const open = () => {
    const url = api.qrSheetUrl(filters);
    window.open(url, "_blank");
  };
  return (
    <div className="p-8 space-y-4" data-testid="qr-sheet-page">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 dark:text-slate-100">Print QR Sheet</h1>
      <Card className="p-5 space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300 dark:text-slate-300">
          Generates printable 10cm × 5cm (100mm × 50mm) landscape QR stickers — one per page — for assets matching the filters.
          Each sticker shows the QR + asset ID + name + location + user. In the browser Print dialog set paper size to 100×50mm (or your label roll), margins None, and scale 100%.
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-xl">
          <Field label="Category (optional)">
            <Select value={filters.category_code} onChange={e=>setFilters(f=>({...f,category_code:e.target.value}))} data-testid="qr-filter-category">
              <option value="">All categories</option>
              {categories.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Location (optional)">
            <Select value={filters.location_id} onChange={e=>setFilters(f=>({...f,location_id:e.target.value}))} data-testid="qr-filter-location">
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          </Field>
        </div>
        <Btn onClick={open} data-testid="qr-sheet-open-btn">Open printable QR sheet</Btn>
      </Card>
    </div>
  );
}

// ---------------- Scan redirect ----------------
function ScanRedirect() {
  const { code } = useParams();
  const nav = useNavigate();
  const [publicData, setPublicData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const loggedIn = !!localStorage.getItem("uat_token");

  useEffect(() => {
    if (loggedIn) {
      api.getAssetByCode(code)
        .then(r => nav(`/assets/${r.data.id}`, { replace: true }))
        .catch(e => {
          setErr(e.response?.data?.detail || "Asset not found");
          setLoading(false);
        });
    } else {
      api.publicScan(code)
        .then(r => { setPublicData(r.data); setLoading(false); })
        .catch(e => {
          setErr(e.response?.data?.detail || "Asset not found");
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const signInDeepLink = () => {
    localStorage.setItem("uat_post_login", `/scan/${code}`);
    nav("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
        <Card className="p-6 max-w-sm w-full">
          <div className="text-slate-500 dark:text-slate-400 text-sm text-center">Looking up {code}…</div>
        </Card>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
        <Card className="p-6 max-w-sm w-full text-center">
          <div className="text-rose-600 dark:text-rose-400 text-sm" data-testid="scan-error">
            <strong>{code}</strong>: {err}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
            This QR may be from a different system or the asset was removed.
          </p>
        </Card>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="w-full max-w-md" data-testid="public-scan-view">
        <div className="flex flex-col items-center mb-5">
          <img src="/aampl-logo.png" alt="AAMPL" className="h-10 w-auto mb-2"
               onError={(e)=>{ e.currentTarget.style.display='none'; }} />
          <p className="text-[11px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">Asset summary</p>
        </div>
        <Card className="p-5">
          <div className="text-center mb-4">
            <div className="text-xs font-mono text-slate-500 dark:text-slate-400">{publicData.asset_id}</div>
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-1">{publicData.name || "—"}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{publicData.category_label}</div>
          </div>
          <div className="space-y-1.5">
            <PublicRow label="Status" value={publicData.status} pill={publicData.status === "Active" ? "emerald" : "slate"} />
            <PublicRow label="Location" value={publicData.location_name || "—"} />
            <PublicRow label="Area" value={publicData.area_name || "—"} />
            <PublicRow label="Date of Purchase" value={fmtDMY(publicData.dop) || "—"} />
            <PublicRow label="Source of Purchase" value={publicData.supplier_name || "—"} />
            <PublicRow label="Last service" value={fmtDMY(publicData.last_service_date) || "—"} />
            <PublicRow label="Next service due" value={fmtDMY(publicData.next_service_due) || "—"} />
            <PublicRow label="Warranty expiry" value={fmtDMY(publicData.warranty_expiry) || "—"} />
            <PublicRow label="AMC expiry" value={fmtDMY(publicData.amc_expiry) || "—"} />
          </div>
        </Card>
        <button
          onClick={signInDeepLink}
          className="btn-primary w-full mt-4"
          data-testid="public-scan-sign-in"
        >
          Sign in for full details
        </button>
        <p className="text-center text-[11px] text-slate-400 dark:text-slate-500 mt-4">
          AAMPL · Internal use only
        </p>
      </div>
    </div>
  );
}

function PublicRow({ label, value, pill }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      {pill ? (
        <span className={`pill-${pill}`}>{value}</span>
      ) : (
        <span className="text-xs font-medium text-slate-900 dark:text-slate-100">{value}</span>
      )}
    </div>
  );
}

// ---------------- Router ----------------
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/scan/:code" element={<ScanRedirect />} />
        <Route path="/" element={<Shell><Dashboard /></Shell>} />
        <Route path="/assets" element={<Shell><AssetsPage /></Shell>} />
        <Route path="/assets/:id" element={<Shell><AssetDetail /></Shell>} />
        <Route path="/maintenance" element={<Shell><MaintenancePage /></Shell>} />
        <Route path="/reports" element={<Shell><ReportsPage /></Shell>} />
        <Route path="/qr-sheet" element={<Shell><QrSheetPage /></Shell>} />
        <Route path="/locations" element={<Shell><LocationsPage /></Shell>} />
        <Route path="/areas" element={<Shell><AreasPage /></Shell>} />
        <Route path="/categories" element={<Shell><CategoriesPage /></Shell>} />
        <Route path="/users" element={<Shell><UsersPage /></Shell>} />
        <Route path="/audit" element={<Shell><AuditPage /></Shell>} />
        <Route path="/backup" element={<Shell><BackupPage /></Shell>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
